import { createTheme, styled } from "@mui/material/styles";
import Box from "@mui/joy/Box/Box";
import selectArrowIcon from "../images/icons/selectArrow.svg";
import DatepickerIcon from "../images/icons/DatepickerIcon.svg";
import ErrorIcon from "../images/icons/ErrorIcon.svg";
import CheckFillIcon from "../images/icons/CheckFillIcon.svg";
import CheckedFillIcon from "../images/icons/CheckedFillIcon.svg";
import ActiveArrowIcon from "../images/icons/ActiveArrowIcon.svg";
import CheckOutlineIcon from "../images/icons/CheckOutlineIcon.svg";
import CheckedOutlineIcon from "../images/icons/CheckedOutlineIcon.svg";
import ClosexIcon from "../images/icons/ClosexIconw.svg";
import TourIcon from "../images/icons/TourIcon.svg";
import "../fonts/style.css";
import { Menu, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";

export const filterWhiteColor =
  "invert(100%) sepia(0%) saturate(1%) hue-rotate(203deg) brightness(103%) contrast(101%)";
export const filterBlackColor =
  "invert(0%) sepia(4%) saturate(5958%) hue-rotate(204deg) brightness(90%) contrast(91%)";
export const filterPrimaryColor =
  "invert(40%) sepia(75%) saturate(308%) hue-rotate(160deg) brightness(93%) contrast(91%)";
export const primaryColor = "#44799C";
export const secondaryColor = "#36607C";
export const lightPrimaryColor = "#FEEAFF";
export const lightPrimaryColor2 = "#e3ebf0";
export const brightPrimaryColor = "#0067b8";
export const lightPrimaryColor3 = "#e8ecf5";
export const darkPrimaryColor = "#102959";
export const darkPrimaryColor2 = "#37599c";

export const blueColor = "#3C589F";
export const greenColor = "#4DDBBC";
export const greenTxtColor = "#155724";
export const lightSecondaryColor = "#95fffd";
export const lightBlueBgColor = "#D9E6FF";
export const blueTxtColor = "#19449A";
export const lightGreenBgColor = "#D4EDDA";
export const lightGreenBdrColor = "#C3E6CB";
export const mGrey = "#4F4E4E";
export const darkGrey = "#333333";
export const greyColor = "#666666";
export const lightGreyColor = "#c4c4c4";
export const warnColor = "#B50000";
export const blackColor = "#000000";
export const errorColor = "#E51414";
export const warningColor = "#E59E33";
export const infoColor = "#2F80ED";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
  }
}

export const DRxTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    background: {
      default: "#ebf1f4",
    },
    filter: {
      primary: {
        main: `${filterPrimaryColor}`,
      },
    },

    primary: {
      main: `${primaryColor}`,
    },
    secondary: {
      main: `${secondaryColor}`,
    },
    success: {
      main: "#33B469",
    },

    warning: {
      main: "#E59E33",
    },
    info: {
      main: "#2F80ED",
    },
    error: {
      main: "#E51414",
    },
    action: {
      active: `${primaryColor}`,
      hoverOpacity: 0.7,
      focus: `${primaryColor}`,
      focusOpacity: 1,
      selected: `${primaryColor}`,
      selectedOpacity: 1,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "& .yellow.MuiMenuItem-root": {
          background: "#ffffa4 !important",
        },

        "& div ,  & h1.MuiTypography-root,  & h2.MuiTypography-root,  & h3.MuiTypography-root,  & h4.MuiTypography-root,  & h5.MuiTypography-root, & h6.MuiTypography-root, & label.MuiTypography-root, & span.MuiTypography-root, & div.MuiTypography-root, & p , & button, & input, & select, & textarea, button.MuiButton-root    ":
          {
            fontFamily: [
              '"Segoe UI"',
              "-apple-system",
              "BlinkMacSystemFont",
              "Roboto",
              '"Helvetica Neue"',
              "Arial",
              "sans-serif",
              '"Apple Color Emoji"',
              '"Segoe UI Emoji"',
              '"Segoe UI Symbol"',
            ].join(","),
          },
        "& .Chatbot": {
          "& .chatwidget": {
            "& .MuiCardHeader-root": {
              "& .MuiAvatar-root": {
                "& svg, & img": {
                  filter: "brightness(10)",
                },
              },
            },
          },
        },
        "& body": {
          background: "#ebf1f4",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          "&  .MuiMenu-list li.MuiButtonBase-root.MuiMenuItem-root ": {
            wordBreak: "break-all",
            whiteSpace: "normal",
            lineHeight: "normal",
            fontSize: 14,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: "6px 10px",
          borderRadius: 6,
          fontSize: "14px",
          fontWeight: 400,
          color: "#fff",
          backgroundColor: "#000",
          "& span:before": {
            backgroundColor: "#000",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "normal",
          "&.help": {
            display: "flex",
            alignItems: "center",
            "& a": {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              textAlign: "center",
              lineHeight: "10px",
              color: "#666",
              marginLeft: 10,
              fontSize: 10,
              marginTop: 3,
            },
            "& svg": {
              color: `${warningColor}`,
              width: 18,
              height: 18,
            },
          },
          "&.blink": {
            animation: "blinkingText 1s infinite",
          },
          "&.wrap": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
          "&.gray": {
            color: "#4A4A4A",
          },
          "&.schdrug": {
            height: 34,
            borderRadius: 6,
            padding: "7px 10px",
            background: "#CADCE8",
            border: "1px solid #CCCCCC",
            color: "#000",
            width: "100%",
            boxSizing: "border-box",
          },
        },
        h1: {
          color: `${primaryColor}`,
          fontSize: 48,
          fontWeight: 700,
        },
        h2: {
          color: `${primaryColor}`,
          fontSize: 40,
          fontWeight: 700,
        },
        h3: {
          fontSize: 32,
          color: `${primaryColor}`,
          fontWeight: 700,
        },
        h4: {
          fontSize: 28,
          fontWeight: 700,
        },
        h5: {
          fontSize: 24,
          fontWeight: 700,
        },
        h6: {
          fontSize: 20,
          fontWeight: 500,
          marginBottom: 20,
        },
        subtitle1: {
          fontSize: 18,
          fontWeight: 600,
        },
        subtitle2: {
          fontSize: 16,
          fontWeight: 400,
        },
        body1: {
          fontWeight: 400,
          fontSize: 16,
        },
        body2: {
          fontWeight: 400,
          fontSize: 14,
          "&.ScheduledDrugLabel": {
            background: "#CADCE8",
            border: "1px solid #CCCCCC",
            color: `${primaryColor}`,
            borderRadius: 6,
            padding: "5px 10px",
            fontWeight: 600,
            margin: "-5px 5px -5px 15px",
          },
        },
        button: {
          fontWeight: 400,
          fontSize: 16,
        },
        caption: {
          fontSize: 12,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          root: {
            "&:focus, &:hover": {
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${primaryColor} !important`,
              },
            },
          },
          "&  fieldset legend, & label.MuiInputLabel-root": {
            display: "none",
          },
        },
        select: {
          root: {
            "&:focus, &:hover": {
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${primaryColor} !important`,
              },
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px !important",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 5,
          },

          fontSize: 14,
          fontWeight: 400,
          borderRadius: 10,
          textTransform: "inherit",
          minWidth: 60,
          height: 34,
          padding: "0 10px",
          width: "100%",
          background: "#fff",

          ".MuiOutlinedInput-root": { borderRadius: 6 },
          "&.MuiSelect-select + input + svg  ": {
            fill: "transparent",
            backgroundImage: `url(${selectArrowIcon})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          height: 34,
          borderRadius: 6,
          "& .MuiButtonBase-root": {
            borderRadius: 6,
            backgroundColor: "transparent !important",
          },
          "& .MuiAutocomplete-endAdornment button[title='Open'] svg, .MuiAutocomplete-endAdornment button[title='Close'] svg  ":
            {
              fill: "transparent",
              backgroundImage: `url(${selectArrowIcon})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
            },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&:focus, &:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${primaryColor} !important`,
            },
          },
          "& .MuiOutlinedInput-input": {
            padding: "0 10px",
          },
        },
        input: {
          fontSize: 14,
          fontWeight: 400,
          borderRadius: 6,
          textTransform: "inherit",
          minWidth: 130,
          height: 34,
          padding: "0 10px",
          width: "100%",
          background: "#fff",

          "& .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #ccc",
            borderRadius: 6,
            borderWidth: "1px !important",
          },
          "&.Mui-disabled ": {
            background: "#e1e1e1",
            WebkitTextFillColor: "#000",
            cursor: "not-allowed",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #ccc !important",
            },
          },

          "&.MuiInputLabel-shrink + .Mui-disabled.MuiOutlinedInput-root fieldset":
            {
              border: `1px solid ${primaryColor} !important`,
            },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          "&  img ": {
            filter: `${filterWhiteColor}`,
          },
        },
        containedWarning: {
          color: "#fff",
          "&  img ": {
            filter: `${filterWhiteColor}`,
          },
        },
        containedSecondary: {
          color: "#fff",
          background: `${secondaryColor}`,
        },
        outlinedPrimary: {
          background: "#fff",
          border: `1px solid ${primaryColor}`,
          color: `${primaryColor}`,
          boxShadow: "none",
          ":hover": {
            background: "#fff",
          },
        },
        outlinedSecondary: {
          color: "#000",
          background: "#fff",
          ":hover": {
            background: "#fff",
          },
          border: `1px solid ${secondaryColor}`,
          boxShadow: "none",
        },
        sizeLarge: {
          fontSize: 14,
          fontWeight: 600,
          height: 42,
          minWidth: 110,
          hoverOpacity: 1,
          padding: "7px 14px",
        },
        sizeMedium: {
          fontSize: 14,
          fontWeight: 600,
          height: 30,
          hoverOpacity: 1,
          boxShadow: "none",
          padding: "7px 14px",
        },
        sizeSmall: {
          fontSize: 14,
          fontWeight: 400,
          height: 27,
          marginRight: 5,
          color: "#fff",
          hoverOpacity: 1,
          boxShadow: "none",
          padding: "4px 10px",
        },
        root: {
          borderRadius: 6,
          textTransform: "inherit",
          boxShadow: "none",
          padding: 5,
          whiteSpace: "nowrap",
          "&.fullw ": {
            width: "100%",
          },
          "&.xs ": {
            fontSize: 12,
            height: 22,
          },
          "&.error ": {
            background: "#B50000",
          },
          "&.error2 ": {
            background: "#860404",
          },
          "&.primary ": {
            background: `${primaryColor}`,
          },
          "&.blue ": {
            background: "#4D5B8D",
          },
          "&.MuiButton-text ": {
            background: "transparent",
            color: `${primaryColor}`,
          },
          "&.grey": {
            background: "#666666 ",
          },
          "&.brightgreen": {
            background: "#36A692",
          },
          "&.green": {
            background: "#526662",
          },
          "&.w150": {
            width: 150,
            maxWidth: "100%",
          },
          "&.w140": {
            width: 140,
          },
          "&.w136": {
            width: 140,
          },
          "&.tab": {
            background: "#fff",
            color: `${primaryColor}`,
            padding: "10px 18px",
            borderRadius: "0 0 10px 10px",
            fontWeight: 600,
            lineHeight: "16px",
            minHeight: 38,
          },
          "&.tab:hover, &.tab.active": {
            background: `${primaryColor}`,
            color: "#fff",
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          "& .MuiCalendarPicker-root .MuiButtonBase-root:hover, .MuiCalendarPicker-root  .Mui-selected":
            {
              color: "#fff",
            },
          "& .textarea78 textarea": {
            height: "78px !important",
            width: "100%",
            boxSizing: "border-box",
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          "&.headersec": {
            padding: "0  !important",
            minHeight: "70px !important",
            margin: "0 !important",
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.tooltip": {
            "& img ": {
              width: 16,
              height: 16,
              filter: `${filterPrimaryColor}`,
            },
          },
          "&.contained-square": {
            borderRadius: 5,
            background: `${primaryColor}`,
            color: "#fff",
          },
          "&.tooltip.ds:hover svg": {
            color: "#fff !important",
          },
          "&.contained.error": {
            background: `${errorColor}`,
            color: "#fff !important",
          },
          "&.contained.warning": {
            background: `${warningColor}`,
            color: "#fff !important",
          },
          "&.contained.info": {
            background: `${infoColor}`,
            color: "#fff !important",
          },
          "&.contained.darkgreen": {
            background: "#526662",
            color: "#fff !important",
          },
          "&.trash img": {
            width: 24,
            filter: `${filterPrimaryColor}`,
          },
          "&.email img, &.phone img": {
            filter: `${filterPrimaryColor}`,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&   .MuiTypography-root": {
            color: `${blackColor}`,
          },
          "&.MuiCheckbox-root.Mui-disabled  svg ": {
            opacity: ".5",
          },
          "&.Mui-checked.Mui-disabled   svg": {
            opacity: ".5",
          },
          "&.MuiCheckbox-root ": {
            "&:hover": { background: "#fafafa" },
          },
          "&.MuiCheckbox-root + .MuiTypography-root": {
            color: `${blackColor}`,
            fontSize: 14,
            margin: 0,
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          "& button": { padding: 0, border: "none", background: "transparent" },
          "&  .MuiStepConnector-root   ": {
            display: "none",
          },
          "& .MuiStep-root.Mui-disabled": {
            "& .MuiStepLabel-iconContainer  ": {
              "& .MuiStepIcon-text ": {
                fill: "#000",
              },
            },
            "& .MuiSvgIcon-root  ": {
              "& .MuiStepIcon-text ": {
                fill: "#000",
              },
            },
          },
          "& .MuiStep-root .Mui-disabled .MuiStepLabel-labelContainer": {
            "&:after ": {
              background: `${greenColor}`,
            },
          },
          "& .MuiStep-root   .MuiStepLabel-labelContainer": {
            "&:after ": {
              background: `${greenColor}`,
            },
          },
          "& .MuiStep-root .MuiStepLabel-labelContainer": {
            "&:after ": {
              zIndex: 0,
              position: "absolute",
              content: `""`,
              left: "-15px",
              right: "-25px",
              top: 11,
              opacity: "0.5",
              height: 2,
              width: "auto",
            },
          },
          "& .MuiStep-root:last-child .MuiStepLabel-labelContainer": {
            paddingRight: 0,
            "&:after ": {
              right: 0,
            },
          },
          "& .MuiStep-root": {
            padding: 0,
            color: `${greenColor}`,
            "& .MuiStepLabel-iconContainer": {
              zIndex: 1,
              "& .MuiStepIcon-text ": {
                fill: "#000",
              },
            },
            "& .MuiStepLabel-labelContainer": {
              padding: "0 10px",
              position: "relative",
            },
            "& .MuiStepLabel-label ": {
              color: "#000",
              background: "#f4f4fc",
              zIndex: 222,
              padding: "0 5px",
              position: "relative",
              fontWeight: 600,
            },
            "& .MuiStepLabel-iconContainer  ": {
              padding: 0,
            },
            "& .MuiStepLabel-root .MuiStepLabel-labelContainer .MuiStepLabel-label.Mui-disabled ":
              {
                color: "#000",
              },

            "& .MuiSvgIcon-root  ": {
              width: 50,
              height: 50,
              color: `${greenColor}`,
              "& .MuiStepIcon-text ": {
                fill: "#000",
                fontSize: 9,
              },
              "&.Mui-active ": {
                color: `${greenColor}`,
                "& .MuiStepIcon-text ": {
                  fill: "#000",
                },
              },
              "&.Mui-complieted ": {
                color: `${greenColor}`,
              },
            },
            "& .MuiSvgIcon-root.Mui-completed ": {
              color: `${greenColor}`,
            },
            "& .Mui-disabled .MuiSvgIcon-root  ": {
              color: "#D9D9D9",
              "& .MuiStepIcon-text ": {
                fill: "#000",
              },
            },
            "& .Mui-completed .MuiStepLabel-root .MuiStepLabel-iconContainer .Mui-completed":
              { color: `${greenColor}` },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            borderRadius: 10,
          },
          "& .MuiDialogContent-root": {
            padding: 20,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingRight: 50,
          background: `${primaryColor}`,
          color: "#fff",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          background: "#fff",
          border: `1px solid ${errorColor}`,
        },
        standardInfo: {
          background: "#D6D8DB",
          color: "#383D41",
          borderRadius: 6,
          "& svg": {
            color: "#383D41",
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          "&.LeftNav": {
            position: "relative",
            zIndex: 1,
            marginTop: 0,

            "&.MuiMenu-list": {
              paddingTop: 0,
              borderRadius: 10,
            },
            "& .MuiListItemIcon-root": {
              minWidth: 30,
              display: "flex",
              flexDirection: "column",
              "& .MuiTypography-root.icontext  ": {
                fontSize: 11,
                lineHeight: "13px",
                padding: 0,
              },
            },

            "& .sidebar-toggle": {
              display: "none",
            },

            "& .MuiMenuItem-root": {
              background: "#fff",
              border: `1px solid ${lightGreyColor}`,
              borderTop: "none",
              color: `${primaryColor}`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "6px 14px",
              minHeight: 34,
              boxSizing: "border-box",
              "& svg": {
                width: 22,
              },
              "& img": {
                width: 22,
                height: "auto",
                maxHeight: 24,
                filter: `${filterPrimaryColor}`,
              },
              "& svg path": { color: `${primaryColor}` },
              "&.hide": {
                display: "none",
              },
              "& svg.lockIcon": {
                display: "none",
              },
              "&.lock.active ": {
                position: "relative",
                "& svg.lockIcon  ": {
                  display: "block",
                  fill: `${warnColor}`,
                  width: 20,
                  position: "absolute",
                },
                background: "#fff",
                color: `${primaryColor}`,
                "& svg path": { color: `${primaryColor}` },
                "& img": { filter: `${filterPrimaryColor}` },
                "& .MuiListItemIcon-root": {
                  position: "relative",

                  "& .MuiTypography-root  ": {
                    color: `${primaryColor}`,
                  },
                },
                cursor: "not-allowed",
              },
              "&.active, &:hover": {
                background: `${primaryColor}`,
                backgroundImage: `url(${ActiveArrowIcon})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "94% center",
                color: "#fff",
                "& svg path": { color: "#fff" },
                "& img": { filter: `${filterWhiteColor}` },
                "& .MuiListItemIcon-root": {
                  "& .MuiTypography-root  ": {
                    color: "#fff",
                  },
                },
              },
            },

            "& .MuiMenuItem-root.submenu.active": {
              background: "#000  ",
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {},
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {},
      },
    },
  },
});

export const SignGrid = styled("div")({
  height: "100%",
  overflow: "auto",
  boxSizing: "border-box",
  "& .gridcontainer": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "auto",
  },
  "& .eye": {
    position: "absolute",
    right: 10,
    top: 22,
  },
  "& .eye:hover": {
    color: "#fff",
  },
  "@media (min-width:767px)": {
    padding: "10px 20px",
  },
  "@media (max-width:767px)": {
    "& .gridcontainer": {
      flexDirection: "column",
      maxWidth: "90%",
    },
  },
});
export const LeftItem = styled("div")(({ theme }) => ({
  background: "#fff",
  marginTop: 10,
  padding: 10,
  display: "flex",
  flexDirection: "column",
  textAlign: "left",
  justifyContent: "center",
  alignItems: "center",

  "& .MuiTypography-subtitle1": { marginBottom: 20 },
  "& .signbtn": {
    marginTop: 20,
  },
  "& .link": {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "none",
  },
  "@media (min-width:767px)": {
    minHeight: 570,
    width: "42%",
    borderRadius: "20px 0 0 20px",
    paddingTop: 30,
    paddingBottom: 30,
  },
  "@media (max-width:767px)": {
    width: "100%",
    borderRadius: "  20px 20px 0 0 ",
    padding: 10,
  },
}));

export const ContentArea = styled("div")({
  width: 345,
  maxWidth: "90%",
  margin: "auto",
  display: "flex",
  textAlign: "left",
  flexDirection: "column",
  justifyContent: "left",
  alignItems: "left",
});

export const RightItem = styled("div")({
  background:
    "linear-gradient(268.44deg, " +
    `${darkPrimaryColor2}` +
    " -44.5%, " +
    `${darkPrimaryColor}` +
    " 82.36%)",
  padding: 10,
  textAlign: "center",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "#fff",

  "@media (min-width:767px)": {
    minHeight: 570,
    width: "48%",
    marginTop: 10,
    borderRadius: "  0 20px 20px 0",
    paddingTop: 30,
    paddingBottom: 30,
  },
  "@media (max-width:767px)": {
    width: "100%",
    borderRadius: " 0 0 20px 20px ",
    padding: 10,
  },
});

export const SignInLogo = styled("div")(({ theme }) => ({
  padding: 10,
  [theme.breakpoints.up("md")]: {
    marginBottom: 60,
  },
  "& img": {
    width: 180,
    maxWidth: "100%",
  },
  textAlign: "center",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 100,
}));

export const ButtonArea = styled("div")({
  width: 345,
  maxWidth: "100%",
  marginTop: 20,
  marginBottom: 20,
  display: "flex",
  textAlign: "left",
  justifyContent: "space-between",
  alignItems: "center",
});

export const LayoutStyle = styled("div")(({ theme }) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  position: "relative",
  alignItems: "stretch",
  justifyContent: "stretch",
  "& form": { width: "100%" },
  "& .MuiBox-root": {
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  "& .contentarea": {
    padding: "10px 20px 20px 20px",
    background: "#fff",
    borderRadius: 10,
    position: "relative",
    zIndex: "0",
    maxWidth: "100%",
    [theme.breakpoints.up("md")]: {
      minHeight: 684,
    },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  "&  .contentarea + div": {
    background: "transparent",
    paddingTop: 60,
    paddingBottom: 10,
  },
  "&  .btnsec ": {
    display: "flex",
    marginTop: 5,
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-end",
      marginTop: 20,
    },
  },

  "& .sidebar ": {
    width: 76,
    minWidth: 76,
    marginLeft: 5,
    marginRight: 10,
    marginBottom: 8,
    position: "relative",
    "& .MuiList-root": {
      borderRadius: 6,
      paddingBottom: 0,
      boxShadow: "rgb(0 0 0 / 10%) 0px 2px 4px",
      "& .MuiMenuItem-root:first-of-type ": {
        borderRadius: "6px 6px 0 0 ",
        borderTop: "1px solid #c4c4c4",
      },
      "& .MuiMenuItem-root:last-child ": { borderRadius: "0 0 6px 6px" },
      "& svg.lockIcon  ": {
        right: -2,
        top: -8,
      },
    },
    "&.open ": {
      "& .icontext": {
        display: "none",
      },

      "& .MuiList-root": {
        "& .MuiMenuItem-root ": {
          minHeight: 34,
          flexDirection: "row",
          textAlign: "left",
          padding: "0 14px",
          "& .MuiListItemText-root  ": {
            display: "inline-block",
          },
          "& .MuiListItemText-root .MuiTypography-root ": {
            fontSize: 13,
            fontWeight: 400,
          },
        },
        "& svg.lockIcon  ": {
          right: 5,
          top: 8,
        },
      },
      [theme.breakpoints.up("md")]: {
        width: 232,
        minWidth: 232,
        "& .PatientInfo.open, & .MuiList-root ": {
          display: "block",
        },
      },
      [theme.breakpoints.down("md")]: {
        width: "100%",
        maxWidth: "100%",
        display: "black",
        "& .PatientInfo.open, & .MuiList-root ": {
          display: "black",
        },
        "& .menuoverlay": {
          left: 0,
        },
        "& .menuoverlaymask": {
          display: "block",
        },
        overflow: "auto",
      },

      "& .PatientInfo.close ": {
        display: "none",
      },
    },
    "&.close ": {
      "& .DocreeAd  ": {
        display: "none",
      },
      [theme.breakpoints.up("md")]: {
        "& .PatientInfo.open  ": {
          display: "none",
        },
      },
      [theme.breakpoints.down("md")]: {
        display: "none",
        "& .PatientInfo.open, & .MuiList-root ": {
          display: "none",
        },
        "& .PatientInfo.close ": {
          display: "none",
        },
      },
      "& .MuiList-root": {
        "& .MuiMenuItem-root ": {
          minHeight: 62,
          flexDirection: "column",
          textAlign: "center",
          padding: 5,
          "& svg, & img": {
            margin: "auto",
            width: 28,
            height: 28,
          },
          "& .MuiListItemText-root   ": {
            display: "none",
          },
          "&.lock:hover .MuiListItemText-root   ": { display: "none" },
          "&:hover .MuiListItemText-root   ": {
            display: "block",
            position: "absolute",
            left: 92,
            width: "auto",
            whiteSpace: "pre",
            background: theme.palette.primary.main,
            padding: "12px 20px",
            height: 50,
            boxSizing: "border-box",
            borderRadius: 5,
            color: "white",
            "&:before": {
              content: '""',
              position: "absolute",
              left: "-16px",
              top: "16px",
              zIndex: "0",
              borderWidth: "8px 16px 10px 16px",
              width: "0",
              height: "0",
              borderColor: "transparent  " + theme.palette.primary.main,
              borderStyle: "solid solid solid none",
            },
          },
        },
      },
    },
    "&.close .ptarea ": {
      "&  .PatientInfo.close   ": { paddingTop: 40 },
      "&:hover .PatientInfo.close + .PatientInfo.open ": {
        display: "block",
        position: "absolute",
        left: 90,
        top: 20,
        width: 232,
        minWidth: 232,
        zIndex: "99",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
        overflow: "visible",
        "&:after": {
          content: '""',
          position: "absolute",
          left: "-18px",
          top: "38px",
          zIndex: "2",
          width: "0",
          height: "0",
          background: "transparent  ",
          borderWidth: "8px 16px 10px 16px",
          borderColor: "transparent  rgba(0, 0, 0, 0.3)",
          borderStyle: "solid solid solid none",
        },
        "&:before": {
          content: '""',
          position: "absolute",
          left: "-16px",
          top: "36px",
          zIndex: "3",
          borderWidth: "8px 16px 10px 16px",
          width: "0",
          height: "0",
          borderColor: "transparent  #fff",
          borderStyle: "solid solid solid none",
        },
      },
    },
    " .ptarea.pdmp ": { position: "relative", marginTop: 8 },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up("md")]: {
      "& .menuoverlay": {
        display: "none",
      },
    },
    "& .menuoverlay": {
      width: 300,
      height: "100%",
      position: "fixed",
      left: -310,
      top: 0,
      bottom: 0,
      background: "#DFE1EE",
      transition: theme.transitions.create(["left", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      zIndex: 2,
    },

    [theme.breakpoints.down("md")]: {
      "& .MuiList-root": {
        width: "auto",
        left: 0,
        zIndex: 3,
        "& > .MuiMenuItem-root": {
          paddingLeft: 40,
        },
      },
    },
  },
  [theme.breakpoints.down("md")]: {
    "& .sidebar.open + .level2    , & .stats.open +   .level3 ": {
      display: "none",
    },
  },

  "& .stats": {
    "&.open": {
      display: "block",
      [theme.breakpoints.down("md")]: {
        overflow: "auto",
      },
    },
    "&.close": {
      display: "none",
    },
  },
  "& .header": {
    [theme.breakpoints.down("md")]: {
      width: "100%",
      minHeight: "inherit",
      flexDirection: "column",
      paddingLeft: 10,
      paddingRight: 10,
    },
    display: "flex",

    justifyContent: "space-between",

    "& .logo  ": {
      marginLeft: 5,
      marginRight: 10,
      position: "relative",
      padding: "10px 0",

      "& img": {
        maxWidth: "100%",
      },
      [theme.breakpoints.down("md")]: {
        padding: 0,
        "& img": {
          height: 40,
        },
      },
    },
    "&.close": {
      overflow: "initial",
      width: 90,
      "& .logo": {
        display: "none",
      },
      "& .logoIcon": {
        padding: 10,
        marginLeft: 5,
        minWidth: 70,
        width: 70,
        position: "relative",
        textAlign: "center",
        mixBlendMode: "multiply",
        color: theme.palette.primary.main,
        "& img": {
          maxWidth: "100%",
        },
      },
    },
    "& .logo": {
      mixBlendMode: "multiply",
    },
    "&.open": {
      overflow: "initial",
      "& .logo": {
        minWidth: 232,
        width: 232,
      },
      "& .logoIcon": {
        display: "none",
      },
    },
  },
  "&  a ": {
    color: theme.palette.primary.main,
    textDecoration: "none",
  },

  "& .level1": {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "stretch",
    [theme.breakpoints.down("md")]: { flex: 1, overflow: "hidden" },
    "& .chatclosebtnicon": {
      borderRadius: "50%",
      backgroundImage: `url("${ClosexIcon}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: 20,
      backgroundColor: `${warningColor}`,
      width: 36,
      height: 36,
      cursor: "pointer",
      [theme.breakpoints.up("md")]: {
        position: "fixed",
        bottom: 10,
        right: -100,
        display: "none",
        transition: "all 0.16s ease-in-out 0s",
        "&.active": {
          right: 10,
          display: "block",
        },
      },
      [theme.breakpoints.down("md")]: {
        position: "fixed",
        top: 2,
        right: 6,
        display: "none",
        backgroundColor: "transparent",
        "&.active": {
          display: "block",
        },
      },
      zIndex: 99999999999999,
    },
    "& .chatbtn": {
      width: 36,
      height: 36,
      cursor: "pointer",
      [theme.breakpoints.up("md")]: {
        position: "absolute",
        top: 654,
        right: 14,
      },
      [theme.breakpoints.down("md")]: {
        position: "fixed",
        bottom: 0,
        right: 14,
      },
    },
  },
  "& .level2": {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    position: "relative",
    alignItems: "stretch",
    justifyContent: "stretch",
    [theme.breakpoints.down("md")]: { flex: 1, overflow: "hidden" },
  },
  "& .level3": {
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    boxSizing: "border-box",
    position: "relative",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 5,
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      flex: 1,
      overflow: "hidden",
      gap: 0,
    },
  },
  "& .level4": {
    height: " 100%",
    width: " 100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    position: "relative",
    alignItems: "stretch",
    justifyContent: "stretch",
    border: "1px solid #DFDDDD",
    background: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    borderRadius: 6,
    [theme.breakpoints.down("md")]: { flex: 1, overflow: "auto" },

    "&.printMode": {
      "& .MuiDataGrid-root .MuiDataGrid-virtualScroller ": {
        overflow: "visible !important",
      },
      "  & .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent ": {
        minHeight: "inherit !important",
        maxHeight: "inherit !important",
      },
      "& .refills .MuiDataGrid-columnHeader , & .rxalert .MuiDataGrid-columnHeader":
        {
          display: "none",
        },
      "  & .PDoctor.MuiDataGrid-row ": {
        border: "1px solid #c4c4c460",
      },
      "& .MuiDataGrid-row  ": {
        background: "#c4c4c41f ",
        border: "1px solid #c4c4c460",
      },
      "& .MuiDataGrid-main, & .MuiDataGrid-main *  ": {
        overflow: "visible !important",
        height: "max-content !important",
      },
      "& .MuiDataGrid-main .MuiDataGrid-cellContent": {
        height: "max-content !important",
      },
      "& .MuiDataGrid-main .MuiDataGrid-columnHeaders, & .MuiDataGrid-main .MuiDataGrid-columnHeaders + div, & .MuiDataGrid-main .MuiDataGrid-virtualScrollerRenderZone":
        {
          position: "relative  !important",
          height: "max-content !important",
        },
      "& .MuiDataGrid-main .MuiDataGrid-virtualScrollerContent": {
        height: "max-content !important",
      },
      "& .full": {
        maxHeight: "inherit !important",
      },
      "& .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent": {
        minHeight: "inherit !important",
      },
      "& .MuiDataGrid-main   .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.refills  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.rxalert  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.report  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent":
        {
          minHeight: "inherit !important",
        },

      "&  .MuiDataGrid-root  .MuiTypography-root ": {
        display: "flex",
        alignItems: "center",
      },
    },
  },
}));
export const PatientInfoStyle = styled("div")(({ theme }) => ({
  border: "1px solid #DFDDDD",
  background: "#fff",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)",
  marginBottom: 8,
  padding: 10,
  color: `${blackColor}`,
  borderRadius: 6,
  boxSizing: "border-box",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",

  "& .MuiAvatar-root": {
    margin: "auto",
    background: theme.palette.primary.main,
    color: "#fff",
  },
  "& .icons": {
    display: "flex",
    margin: "0 0 0 -8px",

    "& svg": {
      height: 16,
    },
  },

  "& .MuiIconButton-root": {
    "& svg, & img": {
      filter: theme.palette.filter.primary.main,
    },
    "&:hover": {
      background: "#f3f3f3",
    },
  },
  "&.close": {
    marginBottom: 1,
    borderRadius: 10,
    textAlign: "center",
    padding: 10,
    "& .MuiTypography-body2": {
      marginBottom: 5,
      "& .MuiTypography-body2": {
        marginBottom: 2,
      },
    },
  },
  "& .MuiTypography-root": { marginBottom: 5, lineHeight: "17px" },
  "& .MuiTypography-root.heading": {
    position: "relative",
    paddingRight: 40,
    "& svg, & img": {
      position: "absolute",
      right: 0,
      top: 5,
      filter: theme.palette.filter.primary.main,
    },
  },
  "& .MuiTypography-subtitle1": { lineHeight: "22px" },
  "& .MuiTypography-body2": {
    fontSize: 13,
    marginBottom: 5,
    color: "#4A4A4A",
    "& strong": { marginRight: 2, display: "inline-block" },
    "& a": { color: `${blackColor}`, fontSize: 13 },
    "& span:first-of-type": { marginRight: 5 },
  },
  "& .MuiButton-root": {
    width: "auto",
  },
  "& .locationIcon, & .locationIcon:hover": {
    marginLeft: 5,
    background: theme.palette.primary.main,
    color: "#fff",
    width: 32,
    height: 32,
  },
  "& .email": {
    "& .MuiButtonBase-root": {
      marginTop: 2,
    },
  },
}));
export const StatsInfoStyle = styled("div")(({ theme }) => ({
  border: "1px solid #DFDDDD",
  background: "#fff",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  borderRadius: 6,
  boxSizing: "border-box",
  maxWidth: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  marginBottom: 10,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },

  "& .ptinfo": {
    display: "flex",
    alignItems: "center",
    opacity: 0.5,
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  "& .MuiTypography-body1": {
    "& strong": { marginRight: 5, display: "inline-block" },
    marginRight: 10,
  },
  [theme.breakpoints.up("md")]: {
    padding: "0 10px",
  },
}));
export const ContainerArea = styled("div")({
  paddingLeft: 20,
  paddingRight: 20,
  height: "100%",
});

export const SigninCarouselStyle = styled("div")({
  width: "100%",
  padding: 0,
  textAlign: "center",
  "& img": {
    maxWidth: "100%",
  },
  "& .MuiTypography-h2": {
    color: "#ffffff",
    fontSize: 23,
    fontWeight: 600,
    marginTop: 20,
  },
  "& .MuiTypography-subtitle1": {
    fontSize: 16,
    fontWeight: 350,
    width: "90%",
    margin: "auto",
    lineHeight: "22px",
    color: "#ffffffcc",
  },
});

export const BtnIconStyle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: 40,
  position: "relative",
  "& .MuiIconButton-root": {
    height: "100%",
    width: "100%",
    transition: theme.transitions.create("background", {
      duration: theme.transitions.duration.standard,
    }),
  },
  "& .MuiIconButton-root:hover": {
    color: "#fff",
    "& a": {
      color: "#fff",
    },
  },
  // "&.primary": {
  //   "& svg": {
  //     color: theme.palette.primary.main,
  //   },
  //   "& img ": {
  //     filter: `${filterPrimaryColor}`,
  //   },
  // },
  "&.toggle": {
    position: "absolute",
    right: 0,
    top: 0,
    // "& img ": {
    //   filter: `${filterPrimaryColor}`,
    // },
    "& .MuiIconButton-root:hover": {
      color: "#000",
      "& a": {
        color: "#000",
      },
    },
  },
  "& .MuiBadge-badge": {
    right: "3px !important",
    top: "-2px  !important",
  },
}));
export const StatsBtnIconStyle = styled("div")(({ theme }) => ({
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 0,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    border: "1px solid #c4c4c4",
    "& a ": {
      width: "100%",
    },
  },
  "& .MuiTypography-root ": {
    fontSize: 12,
    lineHeight: "16px",
    textAlign: "center",
    padding: 0,
    fontWeight: 400,
    color: "#666",
    marginTop: 3,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  },
  "& .MuiButtonBase-root ": {
    transition: theme.transitions.create("background", {
      duration: theme.transitions.duration.standard,
    }),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.up("md")]: {
      flexDirection: "column",
    },
  },

  "& .MuiIconButton-root": {
    position: "relative",
    padding: "12px 10px 8px 10px",
    borderRadius: 0,
    [theme.breakpoints.up("md")]: {
      minHeight: 79,
    },

    [theme.breakpoints.down("md")]: {
      width: "100%",
      justifyContent: "flex-start",
      gap: 10,
    },
  },
  "& .lock.active .MuiIconButton-root ": {
    background: "#fff",
    color: theme.palette.primary.main,
    cursor: "not-allowed",
    "&  img": {
      filter: "none",
    },
    "& .MuiTypography-root ": {
      color: "#666",
    },
  },
  "& .active .MuiIconButton-root, & .MuiIconButton-root:hover": {
    background: theme.palette.primary.main,
    color: "#fff",
    "&  svg": {
      color: "#fff",
    },
    "&  img": {
      filter: `${filterWhiteColor}`,
    },
    "& .MuiTypography-root ": {
      color: "#fff",
    },
  },
  "&  svg, &  img": {
    color: "#000",
    maxHeight: 24,
    minHeight: 24,
  },

  "& .single .MuiBadge-badge": {
    [theme.breakpoints.up("md")]: {
      right: "inherit",
    },
    [theme.breakpoints.down("md")]: {
      right: 10,
      transform: "translate(0, -50%)",
    },

    "&:before": {
      display: "none",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  },
  "& .double .MuiBadge-badge": {
    width: 50,
    [theme.breakpoints.down("md")]: {
      width: "auto",
      fontSize: 14,
    },
  },
  "& .MuiBadge-root": { position: "static" },
  "& .MuiBadge-badge": {
    background: "#fff",
    color: "#333",
    fontWeight: 600,
    padding: 0,
    display: "flex",
    justifyContent: "space-between",
    height: 20,
    overflow: "hidden",
    boxShadow: "-1px -1px 3px #00000042",
    outline: "1px solid #0000001a",
    [theme.breakpoints.up("md")]: {
      left: "50%",
      top: 0,
      transform: "translate(-50%, -50%)",
    },
    [theme.breakpoints.down("md")]: {
      right: 10,
      top: "50%",
      transform: "translate(0, -50%)",
    },

    "&:before  ": {
      content: `""`,
      position: "absolute",
      height: "auto",
      top: 0,
      bottom: 0,
      width: 2,
      borderRight: "1px solid #ffffffab",
      borderLeft: "1px solid #00000038",
      left: "49%",
    },

    "&  span": {
      display: "inline-flex",
      width: 25,
      height: 20,
      justifyContent: "center",
      alignItems: "center",
    },
  },
  "&.count1 .MuiBadge-badge span:first-child": {
    background: theme.palette.warning.main,
    color: "#fff",
  },
  "&.count2 .MuiBadge-badge span:last-child": {
    background: theme.palette.warning.main,
    color: "#fff",
  },
}));
export const FooterStyle = styled("div")(({ theme }) => ({
  padding: 10,
  textAlign: "center",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.up("md")]: {
    columnGap: 10,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    rowGap: 10,
  },
  "&   .MuiTypography-root ": {
    display: "flex",
    columnGap: 10,
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      rowGap: 10,
    },
  },
  "& .MuiTypography-root, & a": {
    fontSize: 13,
    color: `${blackColor}`,
  },
}));

export const StatsCardStyle = styled("div")(() => ({
  display: "inline-block",
  marginTop: 8,
  marginRight: 16,
  marginBottom: 8,
  height: 65,
  "& .card": {
    boxSizing: "border-box",
    padding: "8px 6px",
    borderRadius: 6,
    height: 63,
    width: 180,
    display: "flex",
  },
  "& .icon": {
    borderRadius: 6,
    height: 45,
    width: 45,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,
  },
  "& .MuiTypography-body2": {
    lineHeight: "16px",
  },
  "& h6 ": {
    minWidth: 40,
  },
  "& h6 + h6": {
    paddingLeft: 20,
    position: "relative",
  },
  "& h6 + h6:before": {
    content: `""`,
    position: "absolute",
    width: 1,
    height: 20,
    left: 0,
    top: 6,
    borderLeft: "1px solid #000",
  },
}));

export const NavigationStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    zIndex: 1,
  },
}));

export const InputSMBoxStyle = styled(Box)(({ theme }) => ({
  "& .MuiBox-root.icon button.MuiButtonBase-root.MuiIconButton-root": {
    position: "absolute",
    margin: 3,
    top: 35,
    right: 0,
    zIndex: 1,
    background: "#EDF3F7",
    width: 34,
    height: 34,
    color: theme.palette.primary.main,
    boxShadow: "none",
    borderRadius: 6,
    "&:hover ": {
      background: theme.palette.primary.main,
      color: "#fff",
      "& img ": {
        filter: `${filterWhiteColor}`,
      },
    },
  },
  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },

  "&.labelmr": {
    " & label.MuiTypography-root ": {
      marginTop: "0 !important",
    },
  },
  "&.widthauto": {
    width: "auto",
    " & .MuiBox-root ": {
      width: "100%",
    },
    " & label.MuiTypography-root ": {
      width: "106px",
    },
  },
  [theme.breakpoints.up("md")]: {
    "& .mr": {
      marginLeft: 20,
    },
    "& .mm": {
      height: 40,
    },
    "& .error": {
      marginBottom: 5,
    },
  },
  display: "flex",
  marginBottom: 6,
  "&   .MuiOutlinedInput-notchedOutline": {
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",

    // "&   label.MuiTypography-root": {
    //   display: "none",
    // },

    " & .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
      display: "block !important",
    },
    "&   .MuiOutlinedInput-notchedOutline": {
      top: "-5px !important",
    },
  },
  [theme.breakpoints.up("sm")]: {
    // width: 470,
  },
  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "flex-start",
  "&  .MuiFormControl-root": {
    width: "100%",
    maxWidth: "100%",
    marginTop: 0,
    marginBottom: 0,
    "&  .MuiFormControl-root": {
      marginBottom: 0,
    },
    [theme.breakpoints.down("md")]: {
      "&  .MuiFormControl-root": {
        marginBottom: 0,
      },
    },
  },
  "&  .MuiOutlinedInput-root ": {
    width: "100%",
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      "&  .MuiInputBase-input ": { minWidth: "inherit" },
    },
  },
  "&  .info ": {
    marginLeft: 10,
  },
  " &  .MuiBox-root ": {
    position: "relative",
    "& .MuiTypography-root ": {
      marginTop: 5,
    },
    "& .MuiTypography-root.mr ": {
      marginRight: 5,
    },
  },
  " & > .MuiBox-root ": {
    width: "100%",
    maxWidth: "100%",
    "& .tooltip": {
      position: "absolute",
      right: "-30px",
      top: 3,
      "&:hover svg": {
        color: "#fff",
      },
    },
  },
  "& label.MuiTypography-root ": {
    lineHeight: "normal",
    marginTop: 5,
    minWidth: 120,
    marginRight: 0,
    "& label.MuiTypography-root  ": {
      lineHeight: "normal",
    },
  },

  [theme.breakpoints.up("md")]: {
    "& label.MuiTypography-root.suffix ": {
      minWidth: 80,
    },
  },
  "& > .nested   ": {
    width: "100%",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "&  .MuiFormControl-root": {
      // width: 110
    },
    "&  .MuiOutlinedInput-root ": {
      // width: 110,
    },
    "&  .MuiInputBase-input": {
      minWidth: "inherit",
    },
  },
  "& > .nested > .nested   ": {
    // width: 200,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  "& .nested   .MuiFormControl-root": { width: 110 },
  "&.newticket label.MuiTypography-root": { minWidth: 135 },
}));

export const InputBoxStyle = styled(Box)(({ theme }) => ({
  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },

  "& .iconlabel": {
    marginTop: "-15px !important",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  "& .subinput": {
    display: "flex",
    justifyContent: "space-between",
    "& .MuiFormControl-root ": { width: "auto !important" },
    "& .MuiTypography-root  ": { marginTop: "0 !important" },
    "& .MuiCheckbox-root": { padding: "5px" },
  },
  [theme.breakpoints.up("md")]: {
    "& .mr": {
      marginLeft: 20,
    },
    "& .ml": {
      marginLeft: 20,
    },
    "& .mm": {
      height: 40,
    },
    "& .error": {
      marginBottom: 5,
    },
  },
  display: "flex",
  marginBottom: 20,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",

    "&   label.MuiTypography-root": {
      display: "none",
    },

    " & .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
      display: "block !important",
    },
    "&   .MuiOutlinedInput-notchedOutline": {
      top: "-5px !important",
    },
  },
  width: 600,
  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "flex-start",
  "&  .MuiFormControl-root": {
    width: 365,
    maxWidth: "100%",
    marginTop: 0,
    marginBottom: 0,
    "&  .MuiFormControl-root": {
      marginBottom: 0,
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: 10,
      "&  .MuiFormControl-root": {
        marginBottom: 0,
      },
    },
  },
  "&  .MuiOutlinedInput-root ": {
    width: 365,
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      "&  .MuiInputBase-input ": { minWidth: "inherit" },
    },
  },
  "&  .info ": {
    marginLeft: 10,
  },
  " &  .MuiBox-root ": {
    "& .MuiTypography-root ": {
      marginTop: 7,
      color: theme.palette.primary.main,
    },
  },
  " & > .MuiBox-root ": {
    width: 365,
    maxWidth: "100%",
  },
  "& label.MuiTypography-root ": {
    lineHeight: "normal",
    marginTop: 3,
    minWidth: 132,
    "& label.MuiTypography-root  ": {
      lineHeight: "normal",
      minWidth: "initial",
    },
  },
  "& > .nested   ": {
    width: 365,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    "& label.MuiTypography-root  ": {
      minWidth: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "&  .MuiFormControl-root": { width: 110 },
    "&  .MuiOutlinedInput-root ": {
      width: 110,
    },
    "&  .MuiInputBase-input": {
      minWidth: "inherit",
    },
  },
  "& > .nested > .nested   ": {
    width: 220,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  "& .nested   .MuiFormControl-root": { width: 110 },
}));
export const InputASBoxStyle = styled(Box)(({ theme }) => ({
  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },

  display: "flex",
  marginBottom: 20,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",

    "&   label.MuiTypography-root  ": {
      display: "none",
    },

    " & .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
      display: "block !important",
    },
    "&   .MuiOutlinedInput-notchedOutline": {
      top: "-5px !important",
    },
  },
  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "flex-start",
  " &  .MuiBox-root ": {
    "& .MuiTypography-root ": {
      marginTop: 7,
      color: theme.palette.primary.main,
    },
  },

  "& label.MuiTypography-root ": {
    lineHeight: "normal",
    marginTop: 3,
    "& label.MuiTypography-root  ": {
      lineHeight: "normal",
      minWidth: "initial",
    },
  },
}));
export const InputFilterStyle = styled(Box)(({ theme }) => ({
  display: "flex",

  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },
  "& label": {
    marginTop: 5,
  },

  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "center",

  "&  .MuiOutlinedInput-root ": {
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      "&  .MuiInputBase-input ": { minWidth: "inherit" },
    },
  },
}));
export const LabelBoxStyle = styled(Box)(({ theme }) => ({
  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },
  "&.w120 label.MuiTypography-root": {
    minWidth: 120,
    "&  .MuiFormControl-root": {
      width: 300,
    },
  },
  [theme.breakpoints.up("md")]: {
    "& .mr": {
      marginLeft: 20,
    },
    "& .mm": {
      height: 40,
    },
    "& .error": {
      marginBottom: 5,
    },
  },
  display: "flex",
  marginBottom: 20,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",

    "&   label.MuiTypography-root": {
      display: "none",
    },

    " & .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
      display: "block !important",
    },
    "&   .MuiOutlinedInput-notchedOutline": {
      top: "-5px !important",
    },
  },
  width: 600,
  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "flex-start",
  "&  .MuiFormControl-root": {
    width: 365,
    maxWidth: "100%",
    marginTop: 0,
    marginBottom: 0,
    "&  .MuiFormControl-root": {
      marginBottom: 0,
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: 10,
      "&  .MuiFormControl-root": {
        marginBottom: 0,
      },
    },
  },
  "&  .MuiOutlinedInput-root ": {
    width: 365,
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      "&  .MuiInputBase-input ": { minWidth: "inherit" },
    },
  },
  "&  .info ": {
    marginLeft: 10,
  },
  " &  .MuiBox-root ": {
    "& .MuiTypography-root ": {
      marginTop: 0,
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  " & > .MuiBox-root ": {
    width: 365,
    maxWidth: "100%",
  },
  "& label.MuiTypography-root ": {
    lineHeight: "normal",
    marginTop: 0,
    "& label.MuiTypography-root  ": {
      lineHeight: "normal",
    },
  },
  "& > .nested   ": {
    width: 365,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "&  .MuiFormControl-root": { width: 110 },
    "&  .MuiOutlinedInput-root ": {
      width: 110,
    },
    "&  .MuiInputBase-input": {
      minWidth: "inherit",
    },
  },
  "& > .nested > .nested   ": {
    width: 220,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  "& .nested   .MuiFormControl-root": { width: 110 },
}));
export const LabelSMBoxStyle = styled(Box)(({ theme }) => ({
  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },
  "& .tooltip:hover": {
    color: "#fff",
  },
  [theme.breakpoints.up("md")]: {
    "& .mr": {
      marginLeft: 20,
    },
    "& .mm": {
      height: 40,
    },
    "& .error": {
      marginBottom: 5,
    },
  },
  display: "flex",
  marginBottom: 20,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",

    "&   label.MuiTypography-root": {
      display: "none",
    },

    " & .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
      display: "block !important",
    },
    "&   .MuiOutlinedInput-notchedOutline": {
      top: "-5px !important",
    },
  },
  [theme.breakpoints.up("sm")]: {
    width: 470,
  },
  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "flex-start",
  "&  .MuiFormControl-root": {
    width: 300,
    maxWidth: "100%",
    marginTop: 0,
    marginBottom: 0,
    "&  .MuiFormControl-root": {
      marginBottom: 0,
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: 10,
      "&  .MuiFormControl-root": {
        marginBottom: 0,
      },
    },
  },

  " &  .MuiBox-root ": {
    "& .MuiTypography-root ": {
      marginTop: 7,
      color: theme.palette.primary.main,
    },
  },
  " & > .MuiBox-root ": {
    width: 300,
    maxWidth: "100%",
  },
  "& label.MuiTypography-root ": {
    lineHeight: "normal",
    marginTop: 3,
    "& label.MuiTypography-root  ": {
      lineHeight: "normal",
    },
  },
  "& > .nested   ": {
    width: 300,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "&  .MuiFormControl-root": { width: 110 },
    "&  .MuiOutlinedInput-root ": {
      width: 110,
    },
    "&  .MuiInputBase-input": {
      minWidth: "inherit",
    },
  },
  "& > .nested > .nested   ": {
    width: 200,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  "& .nested   .MuiFormControl-root": { width: 110 },
}));
export const ScheduledDrugLabel = styled("div")(({ theme }) => ({
  "& > div ": {
    position: "relative",
    background: "#CADCE",
    border: "1px solid #CCCCCC",
    borderRadius: 20,
    alignItems: "center",
    padding: 5,
    "& label": {
      paddingLeft: 15,
    },
    "& *": {
      color: theme.palette.primary.main + " !important",
    },
  },
}));
export const DatePickerField = styled("div")(({ theme }) => ({
  "& .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
    display: "none",
  },
  "& .MuiIconButton-root:hover": {
    background: "none",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    top: 0,
  },
  "& .MuiOutlinedInput-root": {
    border: "none",
    padding: "0 !important",
    background: "#fff",
  },
  "& .MuiTextField-root .MuiInputLabel-root": {
    marginTop: "-9px !important",
    fontSize: 14,
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink ": {
    marginLeft: "10px !important",
    color: `${darkPrimaryColor}`,
    marginTop: "0 !important",
  },

  "& .MuiInputLabel-shrink + .MuiOutlinedInput-root fieldset legend ": {
    paddingLeft: "1px",
    marginLeft: "10px !important",
  },
  "& .MuiButtonBase-root": {
    marginRight: 5,
  },
  "& .MuiInputAdornment-root svg  ": {
    fill: "transparent",
    backgroundImage: `url(${DatepickerIcon})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
  },
}));
export const BtnSMGroupStyle = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    textAlign: "right",

    "& .MuiButtonBase-root + .MuiButtonBase-root": {
      marginLeft: 14,
    },
  },
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
    "& .MuiButton-root": {
      margin: 10,
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiButton-root": {
      margin: 3,
    },
  },
  "&.mt10 ": {
    marginTop: 10,
  },
}));
export const BtnGroupStyle = styled(Box)(({ theme }) => ({
  display: "flex",

  flexWrap: "wrap",
  gap: 5,

  marginTop: 10,
  maxWidth: "100%",
  "&.center": {
    textAlign: "center",
    justifyContent: "center",
  },
  "&.left": {
    textAlign: "flex-start",
    justifyContent: "flex-start",
  },
  "&.right": {
    width: "100%",
    [theme.breakpoints.up("md")]: {
      textAlign: "right",
      justifyContent: "right",
    },
  },
  "&.wa": {
    width: "100%",
  },
  "&.middle": {
    justifyContent: "center",
    [theme.breakpoints.up("lg")]: {
      width: "100%",
      marginTop: -56,
    },
  },
  [theme.breakpoints.up("md")]: {
    textAlign: "right",
    justifyContent: "right",

    "& .MuiButton-root + .MuiButton-root": {
      marginLeft: 14,
    },
  },
  [theme.breakpoints.between("sm", "md")]: {
    textAlign: "center",
    justifyContent: "center",
    "& .MuiButton-root": {
      margin: 10,
    },
  },
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
    justifyContent: "center",
    "& .MuiButton-root": {
      margin: 1,
    },
  },
}));
export const BtnGroupWAStyle = styled("div")(({ theme }) => ({
  maxWidth: "100%",
  textAlign: "center",
  [theme.breakpoints.up("md")]: {
    "& .MuiButton-root": {
      marginLeft: 14,
    },
    "& .MuiButton-root:first-of-type": {
      marginLeft: 0,
    },
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiButton-root": {
      margin: 10,
    },
  },
}));
export const ToggleStyle = styled("div")(({ theme }) => ({
  "&   > div": {
    background: "transparent",
    boxShadow: "none",
    [theme.breakpoints.up("md")]: {
      marginLeft: "-25px",
    },
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiIconButton-root:hover": {
      color: theme.palette.primary.main + `!important`,
    },
  },
}));

export const TabsNavStyle = styled("div")(({ theme }) => ({
  "&.hide": {
    display: "none",
  },
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorX: "contain",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    height: 4,
  },
  "& .MuiList-root": {
    display: "flex",
    padding: "0 10px",
    marginTop: "",
    justifyContent: "flex-start",
    alignItems: "center",
    flexWrap: "nowrap",
    width: "max-content",
    minWidth: "100%",
    overflow: "visible",
    [theme.breakpoints.down("lg")]: {
      justifyContent: "flex-start",
      gap: 0,
      columnGap: 2,
      padding: 0,
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: 5,
    },
  },
  "& .MuiListItemIcon-root": {
    minWidth: 36,
  },

  [theme.breakpoints.down("md")]: {
    zIndex: 1,
    flexShrink: 0,
  },
  "& .MuiMenuItem-root": {
    background: "#fff",
    color: theme.palette.primary.main,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px 16px",
    borderRadius: "0 0 10px 10px",
    fontWeight: 600,
    lineHeight: "16px",
    textAlign: "center",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    minHeight: 38,
    flexShrink: 0,
    borderTop: "none",
    transition: theme.transitions.create(["background", "padding", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down("lg")]: {
      minHeight: "inherit",
      padding: "5px 10px",
      fontSize: 12,
    },
    [theme.breakpoints.down("sm")]: {
      borderRadius: 6,
    },
  },
  "& .MuiMenuItem-root:hover, & .MuiMenuItem-root.active": {
    background: theme.palette.primary.main,
    color: "#fff",
  },
  "& .MuiMenuItem-root.submenu.active": {
    background: "#000  ",
  },
}));

export const DrugSearchStyle = styled("div")(({ theme }) => ({
  position: "relative",
  "& .MuiIconButton-root": {
    position: "absolute",
    right: "-8px",
    top: 0,
    background: theme.palette.primary.main,
    color: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
    "& svg": {
      width: 20,
      height: 20,
    },
  },
}));
export const NestedBtn = styled("div")({
  position: "relative",
});
export const SIGInputStyle = styled("div")(({ theme }) => ({
  position: "relative",
  "& .MuiIconButton-root": {
    position: "absolute",
    right: "-8px",
    top: 0,
    background: theme.palette.primary.main,
    color: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
    "& svg": {
      width: 30,
      height: 30,
    },
  },
}));
export const PharmacySearchStyle = styled("div")(({ theme }) => ({
  position: "relative",
  "& .MuiIconButton-root": {
    position: "absolute",
    right: "-8px",
    top: 0,
    background: theme.palette.primary.main,
    color: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
    "& svg": {
      width: 20,
      height: 20,
    },
  },
}));

export const FavDrugStyle = styled("div")({
  marginBottom: 10,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: `${lightGreenBgColor}`,
  border: `1px solid ${lightGreenBdrColor}`,
  padding: "4px 10px",
  color: `${greenTxtColor}`,
  borderRadius: 6,
  "& .MuiIconButton-root": {
    color: `${mGrey}`,
    "& svg": {
      color: `${mGrey}`,
    },
    "&:hover svg": {
      color: "#fff",
    },
  },
});

export const SummaryBox = styled(Box)(({ theme }) => ({
  position: "relative",
  background: theme.palette.primary.main + "26",
  borderRadius: 6,
  "&:first-of-type": { marginTop: 0 },
  "& .MuiButtonBase-root.MuiIconButton-root": { padding: 0, marginLeft: 3 },
  marginTop: 15,
  border: "1px solid " + theme.palette.primary.main + "36",
  padding: " 10px 45px  ",
  color: `${blackColor}`,
  "&.pdr0 ": {
    paddingRight: 10,
  },
  "& .checkboxgrp.right": {
    justifyContent: "flex-end",
  },
  "& .checkboxgrp": {
    border: `1px solid  #fff`,
    background: `#ffffff52`,
    boxSizing: "border-box",
    paddingLeft: 15,
    display: "flex",
    justifyContent: "flex-start",
    width: "auto",
    borderRadius: 5,
    marginLeft: "-5px",
    flexWrap: "wrap",
    "& > div": {
      background: "#ffffff6b",
      border: "1px solid " + theme.palette.primary.main + "60",
      borderRadius: 5,
      margin: 3,
    },
    "&  label": {
      paddingLeft: 30,
      flexDirection: "row-reverse",
      marginRight: 0,
    },

    "&  .MuiTypography-root": {
      fontWeight: 600,
    },
  },
  "& .btns": {
    textAlign: "left",
    display: "flex",
    gap: 5,
    flexWrap: "wrap",
    "& .MuiButton-root": {
      boxShadow: "none",
      margin: 0,
    },
  },
  "& .lblval": {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    "&   div": {
      display: "inline-block",
      wordBreak: "break-word",
    },
    "& > label": {
      minWidth: 90,
    },
    "& . MuiTypography-root": { padding: 5 },
  },
  "& .lblval.chk": {
    "& > label": {
      minWidth: 60,
    },
  },
  "& .lblcheckgrp": {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
  },
  "& .lblcheck": {
    background: `${lightPrimaryColor3}70`,
    border: `1px solid ${brightPrimaryColor}70`,
    borderRadius: 5,
    boxSizing: "border-box",
    padding: "2px 8px 2px 6px",
    display: "flex",
    "& .MuiTypography-root": {
      fontSize: 13,
    },
    "&  svg": {
      width: 20,
      height: 20,
    },
    "& .uncheck": {
      color: theme.palette.error.main,
      marginRight: 5,
    },
    "& .checked": {
      color: theme.palette.primary.main,
      marginRight: 5,
    },
  },
  "& .lblval.pr": {
    paddingRight: 20,
  },
  "& .checkbox": {
    position: "absolute",
    left: 10,
    top: 15,
    margin: "-12px 0 0 0",
    "& label": {
      marginRight: 0,
    },
    "&.rxs svg": {
      marginTop: -5,
      marginLeft: -5,
    },
    "& svg": {
      position: "relative",
      zIndex: 1,
      color: theme.palette.primary.main,
    },
    "& .MuiCheckbox-root:before": {
      content: `""`,
      position: "absolute",
      background: "#fff",
      left: 3,
      right: 3,
      top: 3,
      bottom: 3,
      zIndex: 0,
    },
  },
  "& .edit-btn  ": {
    position: "absolute",
    right: 10,
    top: 15,
    "& svg, & img ": {
      width: 12,
      height: 12,
      filter: `${filterWhiteColor}`,
    },
  },
}));

export const SummaryBtns = styled("div")({
  textAlign: "center",
  position: "relative",
  marginTop: 10,
  "& .MuiButton-root": {
    minWidth: "auto",
    margin: 2,
  },
  "& .MuiIconButton-root": {
    minWidth: "auto",
    margin: 2,
  },
});

export const SummaryInfo = styled("div")({
  position: "relative",
  background: "#fff",
  border: "1px solid #ccc",
  padding: "10px 10px 3px 12px",
  borderRadius: 5,
  marginBottom: 10,
  "& .info": {
    background: "#fff",
    minHeight: 100,
    padding: 5,
    border: `1px solid ${lightGreyColor}`,
    marginBottom: 10,
  },
});

export const RxTabs = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  borderBottom: "1px solid #ccc",
  marginBottom: 5,
});

export const RxTab = styled("div")(({ theme }) => ({
  background: `transparent`,
  display: "flex",
  alignItems: "center",
  position: "relative",
  borderRadius: "5px 5px 0 0",
  marginBottom: "-5px",
  borderBottom: "4px solid transparent",
  padding: "0 10px",
  marginRight: 2,

  "& .MuiTypography-root": {
    margin: 0,
    lineHeight: "30px",
    color: "#757575",
  },
  "& button": {
    padding: 0,
    marginLeft: 10,
    lineHeight: "18px",
    width: 18,
    height: 18,
    borderRadius: 20,
    border: "none",
    "&:hover": {
      background: `${errorColor}`,
      "& svg": {
        color: " #fff !important",
      },
    },
  },

  "& svg": {
    width: 10,
    color: "#757575",
  },
  "&.active, &:hover": {
    fontWeight: 600,
    borderBottom: "4px solid" + theme.palette.primary.main,
    borderRadius: 4,
    color: theme.palette.primary.main,
    "& .MuiTypography-root": {
      color: theme.palette.primary.main + " !important",
      fontWeight: 600 + " !important",
    },
  },
}));

export const TabContent = styled("div")({
  paddingTop: 10,
});

export const SummaryBoxScroll = styled("div")({
  maxHeight: 430,
  overflow: "auto",
});

export const DataGridListStyle = styled("div")(({ theme }) => ({
  width: "auto",
  "&.title  ": {
    height: 35,
    maxHeight: 35,
    minHeight: 35,
  },
  "&.inactive .MuiDataGrid-root .MuiDataGrid-row ": {
    background: "#EEEEEE",
  },
  "&.stableMinHeight .MuiDataGrid-row": {
    minHeight: "42px !important",
  },
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "none",
  },
  "& .yellow.MuiDataGrid-row": {
    background: "#ffffa4 !important",
  },
  "& .FTP.MuiDataGrid-row": {
    background: "#F3F781 !important",
  },
  "& .EP.MuiDataGrid-row": {
    background: "#FFC0CB !important",
  },
  "& .OP.MuiDataGrid-row": {
    background: "#FFCC99 !important",
  },
  "& .PDoctor.MuiDataGrid-row": {
    background: theme.palette.primary.main + `  !important`,
    color: "#fff",
    "& .MuiIconButton-root img": {
      filter: `${filterWhiteColor} !important`,
    },
  },
  [theme.breakpoints.up("md")]: {
    "&.half  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.refills  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.rxalert  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.report  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent":
      {
        minHeight: "222px !important",
      },
  },
  "&.refills  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent, &.rxalert  .MuiDataGrid-root .MuiDataGrid-virtualScrollerContent":
    {
      minHeight: "inherit !important",
      // maxHeight: "500px !important",

      // marginBottom: "20px",
    },
  // "&.refills .MuiDataGrid-root .MuiDataGrid-virtualScroller, &.rxalert  .MuiDataGrid-root .MuiDataGrid-virtualScroller":
  //   {
  //     overflow: "visible !important",
  //   },
  "& .MuiDataGrid-root ": {
    border: "none",
    "& *  ": {
      outline: "none !important",
    },
    "& .MuiTypography-root ": {
      // fontSize: 14,
    },
    "& .MuiDataGrid-virtualScrollerRenderZone": {
      [theme.breakpoints.up("lg")]: {
        width: "99.8% !important",
      },
    },
    "& .MuiDataGrid-virtualScroller ": {
      // position:'static',
      paddingBottom: 20,
      overflow: "auto !important",
    },
    [theme.breakpoints.down("md")]: {
      "& .MuiDataGrid-virtualScroller ": {},
    },
    "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type ":
      {
        paddingLeft: "15px !important",
      },
    "& .MuiDataGrid-cell:first-of-type .MuiCheckbox-root ": {
      marginLeft: "-10px",
      marginRight: "3px !important",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer  ": {
      "& .MuiCheckbox-root ": {
        marginLeft: "-11px",
      },
    },
    "&  .MuiDataGrid-columnHeader--filtered .MuiButtonBase-root   ": {
      padding: 0,
    },
    "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root input": {
      borderRadius: 100,
      width: "98%",
    },
    "& .MuiOutlinedInput-root   ": { overflow: "hidden" },
    "& .textwrap.MuiTypography-root": {
      display: "flex",
      overflow: "hidden",
      alignItems: "center",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      "& .MuiTypography-root": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    "& .MuiSvgIcon-root": {
      color: "#666",
    },
    "  & .MuiDataGrid-virtualScrollerContent ": {
      minHeight: "444px !important",
      maxHeight: "500px !important",
    },
    "  & .MuiDataGrid-virtualScrollerContent, .MuiDataGrid-columnHeadersInner div[role='row']":
      {
        width: "100% !important",
      },
    "& .MuiDataGrid-columnHeaders, ": {
      border: "none",

      "& .MuiSvgIcon-root": {
        color: theme.palette.primary.main,
      },
      "& .MuiDataGrid-columnHeader  ": {
        background: "#fff !important ",
      },
      "& .MuiDataGrid-columnHeadersInner  ": {
        width: "100% !important",
      },

      "& .MuiIconButton-root.MuiButtonBase-root:hover ": {
        background: "transparent",
      },
      "& .MuiDataGrid-columnHeaderTitle ": {
        fontSize: 13,
        fontWeight: 600,
        textOverflow: "initial",
        color: theme.palette.primary.main,
        whiteSpace: "initial",
        lineHeight: "normal",
      },
      "& .MuiDataGrid-columnSeparator , & .MuiDataGrid-menuIcon": {
        display: "none",
      },
    },

    "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
      padding: "0 !important",
      border: "none",
      width: "0% !important",
      maxWidth: "inherit !important",
      minWidth: "inherit !important",
    },
    "& .MuiDataGrid-columnHeader.hide, & .MuiDataGrid-cell.hide  ": {
      padding: "0   !important",
      width: "0% !important",
      display: "none",
    },
    "& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type":
      {
        minWidth: "50px !important",
      },
    "& .MuiDataGrid-columnHeader.w8, & .MuiDataGrid-cell.w8  ": {
      padding: "0 5px !important",
      width: "8% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "80px !important",
        maxWidth: "80px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w10, & .MuiDataGrid-cell.w10  ": {
      padding: "0 5px !important",
      width: "10% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "100px !important",
        maxWidth: "100px !important",
      },
    },

    "& .MuiDataGrid-columnHeader.w12, & .MuiDataGrid-cell.w12 ": {
      padding: "0 5px !important",
      width: "12% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "120px !important",
        maxWidth: "120px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w13, & .MuiDataGrid-cell.w13 ": {
      padding: "0 5px !important",
      width: "13% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "120px !important",
        maxWidth: "130px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w15, & .MuiDataGrid-cell.w15 ": {
      padding: "0 5px !important",
      width: "15% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "150px !important",
        maxWidth: "150px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w18, & .MuiDataGrid-cell.w18 ": {
      padding: "0 5px !important",
      width: "18% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "180px !important",
        maxWidth: "180px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w20, & .MuiDataGrid-cell.w20 ": {
      padding: "0 5px !important",
      width: "20% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "200px !important",
        maxWidth: "200px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w25, & .MuiDataGrid-cell.w25 ": {
      padding: "0 5px !important",
      width: "25% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "250px !important",
        maxWidth: "250px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w26, & .MuiDataGrid-cell.w26 ": {
      padding: "0 5px !important",
      width: "26% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "260px !important",
        maxWidth: "260px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w30, & .MuiDataGrid-cell.w30 ": {
      padding: "0 5px !important",
      width: "30% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "280px !important",
        maxWidth: "200px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w35, & .MuiDataGrid-cell.w35 ": {
      padding: "0 5px !important",
      width: "35% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "260px !important",
        maxWidth: "260px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w40, & .MuiDataGrid-cell.w40 ": {
      padding: "0 5px !important",
      width: "40% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "260px !important",
        maxWidth: "260px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w45, & .MuiDataGrid-cell.w45 ": {
      padding: "0 5px !important",
      width: "45% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "300px !important",
        maxWidth: "300px !important",
      },
    },

    "& .MuiDataGrid-columnHeader.w50, & .MuiDataGrid-cell.w50 ": {
      padding: "0 5px !important",
      width: "50% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "300px !important",
        maxWidth: "300px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w55, & .MuiDataGrid-cell.w55 ": {
      padding: "0 5px !important",
      width: "55% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "300px !important",
        maxWidth: "300px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w60, & .MuiDataGrid-cell.w60 ": {
      padding: "0 5px !important",
      width: "60% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "300px !important",
        maxWidth: "300px !important",
      },
    },
    "& .MuiDataGrid-columnHeader.w65, & .MuiDataGrid-cell.w65 ": {
      padding: "0 5px !important",
      width: "65% !important",
      [theme.breakpoints.down("lg")]: {
        minWidth: "300px !important",
        maxWidth: "300px !important",
      },
    },
    "@media print": {
      " & .MuiDataGrid-cell.patientId  ": {
        "& .MuiTypography-root": {
          whiteSpace: "normal",
          wordBreak: "break-word",
        },
      },
    },
    "& .MuiDataGrid-row": {
      width: "100% !important",
      border: `1px solid ${lightGreyColor}60`,
      background: `${lightGreyColor}1f`,
      cursor: "pointer",
      borderRadius: 6,
      marginBottom: 4,
      boxShadow: "none",

      "&.Mui-selected": {
        background: theme.palette.primary.main + "26 !important",
        "&:before  ": {
          border: "1px solid " + theme.palette.primary.main,
        },
      },
      "& .MuiButtonBase-root.tooltip  ": {
        marginRight: 0,
        "& img ": {
          width: 14,
          height: 14,
          filter: theme.palette.filter.primary.main,
        },
      },
      "& .MuiButtonBase-root    ": {
        marginRight: 15,
      },
      "&:hover  ": {
        background: theme.palette.primary.main + "26",
        border: "1px solid " + theme.palette.primary.main,
      },

      "& .MuiIconButton-root.text:hover    ": {
        background: "none",
        color: theme.palette.primary.main + "!important",
      },
      "& .MuiDataGrid-cell, & .MuiDataGrid-cellContent": {
        whiteSpace: "normal !important",
      },
    },

    "& .MuiDataGrid-footerContainer": {
      border: "none",
    },
    "& .MuiIconButton-root": {
      width: 30,
      height: 30,
      "& img ": {
        width: 22,
        height: 22,
        filter: theme.palette.filter.primary.main,
      },
    },
    "& .MuiIconButton-root.text    ": {
      width: "auto",
      height: "auto",
    },
    "& div[aria-label*='filter'] ": {
      display: "none",
    },
  },
}));

export const ContentStyle = styled("div")({
  borderRadius: 10,
  padding: 12,
  width: " 100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  position: "relative",
  alignItems: "flex-start",
  justifyContent: "center",
  background: "linear-gradient(4deg, #fff  0%, #fff  100%)",
  "&.lightprimary": {
    background: `${brightPrimaryColor}2b`,
  },
  "& .btns": {
    textAlign: "center",
    "& .MuiButtonBase-root:first-of-type ": {
      marginLeft: 0,
    },
    "& .MuiButtonBase-root ": {
      marginLeft: 10,
      marginBottom: 10,
    },
  },
});
export const PharmacyCard = styled("div")({
  background: "#fff",
  border: `1px solid ${lightGreyColor}`,
  borderRadius: 10,
  padding: 18,
  overflow: "auto",
  marginRight: 10,
});

export const ConfirmCard = styled("div")({
  display: "flex",
  border: `1px solid ${lightGreyColor}`,
  borderRadius: 20,
  padding: 18,
  overflow: "auto",
  marginBottom: 10,
  "& .icon": {
    marginTop: 10,
    minWidth: 80,
  },
});

export const SingleConfirmCard = styled("div")({
  display: "flex",
  paddingLeft: 0,
  alignItems: "center",
  justifyContent: "flex-start",
  "& .icon": {
    marginTop: "-11px",
    minWidth: 30,
    "& svg": {
      maxWidth: 15,
    },
  },
  "& .icon2": {
    marginTop: "-5px",
    marginLeft: "-5px",
    minWidth: 40,
    "& svg": {
      maxWidth: 25,
    },
  },
  "& .MuiCheckbox-root .MuiButtonBase-root ": {
    margin: 0,
    padding: 0,
    paddingLeft: 10,
    marginRight: 5,
  },
  "&  .MuiButtonBase-root ": {
    padding: "0px 5px 0px 9px",
    marginTop: "-1px",
  },
  "&  .MuiFormControlLabel-root": {
    marginRight: 0,
  },
  "&  .MuiIconButton-root:hover": {
    background: "transparent",

    "& svg": {
      color: `${brightPrimaryColor}`,
    },
  },
  "& .MuiFormControlLabel-root": { alignItems: "flex-start" },
  "& .MuiCheckbox-root + .MuiTypography-root": {
    fontWeight: 600,
  },
});

export const IconTextBtnStyle = styled("div")(({ theme }) => ({
  display: "inline-block",
  verticalAlign: "middle",
  "& a ": {
    minHeight: 80,
    width: 120,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.primary.main,
    borderRadius: 10,
    padding: 8,
    margin: 2,
    boxBorder: "box-sizing",
    "&.active, &:hover": {
      background: theme.palette.primary.main,
      color: "#fff",

      "& img ": {
        filter: `${filterWhiteColor}`,
      },
    },
    "& img ": {
      marginBottom: 10,
      height: 40,
      filter: `${filterPrimaryColor}`,
    },
  },
}));

export const TelephoneStyle = styled("div")({
  display: "flex",
  verticalAlign: "middle",
  gap: 5,
  "& .MuiBox-root  ": {
    display: "flex",
    flexDirection: "column",
    "&  .MuiFormControl-root, & .MuiFormControl-root .MuiInputBase-formControl":
      {
        width: "auto",
        maxWidth: "inherit",
      },
  },
  "& input ": {
    minWidth: "inherit",
  },
});

export const Note = styled("div")({
  padding: 10,
  background: "#FFF5EB",
  textAlign: "center",
  verticalAlign: "middle",
});

export const AlertWarn = styled("div")({
  borderRadius: 6,
  padding: "10px 10px 10px 40px",
  textAlign: "left",
  position: "relative",
  color: "#721C24",
  border: "1px solid #ccc",
  "& svg , & img": {
    position: "absolute",
    left: 10,
    top: 12,
    color: "#721C24",
    filter:
      "brightness(0) saturate(100%) invert(12%) sepia(57%) saturate(2545%) hue-rotate(332deg) brightness(101%) contrast(95%)",
  },
});

export const InfoAlert = styled("div")({
  borderRadius: 6,
  padding: "10px 10px 10px 40px",
  textAlign: "left",
  position: "relative",
  color: blackColor,
  // border: "1px solid #ccc",
  "& svg , & img": {
    position: "absolute",
    left: 10,
    top: 12,
    color: primaryColor,
    filter: filterPrimaryColor,
  },
});

export const AlertPurple = styled("div")({
  borderRadius: 6,
  padding: "10px 10px 10px 40px",
  textAlign: "left",
  position: "relative",
  color: "#6d1899",
  border: "1px solid #ccc",
  "& svg , & img": {
    position: "absolute",
    left: 10,
    top: 12,
    color: "#6d1899",
    filter:
      " brightness(0) saturate(100%) invert(11%) sepia(49%) saturate(6942%) hue-rotate(277deg) brightness(97%) contrast(95%)",
  },
});
export const AlertInfo = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  padding: "10px 10px 10px 40px",
  textAlign: "left",
  position: "relative",
  border: "1px solid #ccc",
  "&.ni": { marginTop: 10, paddingLeft: 15 },
  "& svg , & img": {
    position: "absolute",
    left: 10,
    top: 12,
    color: "#383D41",
    filter:
      "invert(66%) sepia(5%) saturate(0%) hue-rotate(26deg) brightness(92%) contrast(90%)",
  },
  "&.gray": {
    background: "#F6F6F6",
  },
  "&.white": {
    background: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  },
}));

export const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`&.w316 .${tooltipClasses.tooltip}`]: {
    maxWidth: 316,
  },
  [`&.w250 .${tooltipClasses.tooltip}`]: {
    maxWidth: 316,
  },
  [`&.w150 .${tooltipClasses.tooltip}`]: {
    maxWidth: 150,
  },
  [`&.orange .${tooltipClasses.tooltip}`]: {
    backgroundColor: "orange",
    color: "#fff",
    "& span:before": {
      backgroundColor: "orange",
    },
  },
  [`&.green .${tooltipClasses.tooltip}`]: {
    backgroundColor: "green",
    color: "#fff",
    "& span:before": {
      backgroundColor: "green",
    },
  },
  [`&.warning .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "rgb(93, 26, 26)",
    border: "1px solid orange",
    "& span:before": {
      backgroundColor: "#fff",
      border: "1px solid orange",
    },
  },
  [`&.red .${tooltipClasses.tooltip}`]: {
    backgroundColor: "red",
    color: "#fff",
    "& span:before": {
      backgroundColor: "red",
    },
  },
  [`&.ai .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    "& span:before": {
      backgroundColor: "#fff",
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 6,
    color: "#fff",
    maxWidth: 460,
    "& span:before": {
      backgroundColor: theme.palette.primary.main,
    },
    "& .MuiTypography-body2": {
      fontSize: 13,
    },
    "& .MuiTypography-body2.textwrap": {
      textOverflow: "unset",
      whiteSpace: "normal",
    },
    "& .MuiIconButton-root ": {
      "&:hover": {
        color: "#fff !important",
        "& svg": {
          color: "#fff !important",
        },
      },
    },
  },
}));

export const SelectStyle = styled("div")({
  "& .MuiSelect-select": {
    display: "inline-flex !important",
  },
});

export const ListHead = styled("div")(({ theme }) => ({
  background: theme.palette.primary.main + "26",
  border: "1px solid" + theme.palette.primary.main,
  borderRadius: 6,
  padding: 10,
  marginBottom: 10,
  "& .MuiIconButton-root:hover": {
    color: "#fff",
  },
}));

export const InfoCard = styled("div")(({ theme }) => ({
  background: theme.palette.primary.main + "26",
  border: "1px solid" + theme.palette.primary.main,
  borderRadius: 6,
  padding: 10,
  marginBottom: 20,
  "& .MuiGrid-root": {
    "& .MuiGrid-root": {
      "& .MuiTypography-root.ptId , & .MuiTypography-root.ptName": {
        wordBreak: "break-word",
      },
      "& .MuiTypography-root:nth-of-type(1)": {
        marginRight: 6,
        whiteSpace: "nowrap",
      },
    },
  },
}));

export const PatientPharmaInfoCard = styled("div")(({ theme }) => ({
  border: "2px dashed" + theme.palette.secondary.main + "6b",
  borderRadius: 6,
  marginBottom: 20,
  display: "flex",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  "& .patientInfo ": {
    [theme.breakpoints.up("md")]: {
      borderRight: "2px dashed" + theme.palette.secondary.main + "6b",
    },
    [theme.breakpoints.down("md")]: {
      borderBottom: "2px dashed" + theme.palette.secondary.main + "6b",
    },
  },
  "& .pharmacyInfo , & .patientInfo": {
    display: "flex",
    padding: 15,
    flex: 1,
    gap: 20,
    "& .title": {
      display: "flex",

      gap: 5,
      flexDirection: "column",
      alignItems: "start",

      "& .MuiTypography-root": {
        display: "flex",
        flexDirection: "column",
      },
    },
    "& .content": {
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
      alignItems: "start",
      gap: 5,
      wordBreak: "break-word",
      "& strong": {
        fontWeight: 600,
        color: "#000",
        wordBreak: "normal",
      },
      "& span": {
        wordBreak: "break-word",
      },
      "& .MuiTypography-root": {
        display: "flex",
        gap: 5,
        color: "#333",
      },
      "& .sl": {
        display: "flex",
        justifyContent: "center",
        alignItems: "start",
        gap: 10,
      },
    },
  },
}));

export const RxChangePrescribedCard = styled("div")(({ theme }) => ({
  backgroundColor: "#F5F5F5",
  border: "1px solid" + theme.palette.secondary.main + "50",
  borderRadius: "6px",
  padding: "16px",
  paddingTop: "2px",
  flex: 13,
  "& .title": {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid" + DRxTheme.palette.primary.main + "50",
    marginBottom: 10,
    "& h6": { margin: 0, flex: 1 },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTypography-caption": { width: "30%", textAlign: "center" },
    },
  },
  "& .content": {
    position: "relative",
    display: "flex",
    gap: 5,
    flexDirection: "column",
    "& strong": {
      fontWeight: 600,
      color: "#000",
      wordBreak: "normal",
    },
    "& span": {
      wordBreak: "break-word",
    },
    "& .MuiTypography-root": {
      display: "flex",
      gap: 10,
      color: "#333",
    },
    "& .MuiBox-root.sl": {
      display: "flex",
      justifyContent: "start",
      alignItems: "start",
      gap: 10,
      flexWrap: "wrap",
      flexDirection: "row",
    },
  },
}));
export const RxChangeRequestedCard = styled("div")(({ theme }) => ({
  padding: "16px",
  backgroundColor: "#E3EBF0",
  border: "1px solid" + theme.palette.primary.main,
  borderRadius: "6px",
  paddingTop: "2px !important",
  flex: 16,
  "& .title": {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid" + DRxTheme.palette.primary.main + "50",
    marginBottom: 10,
    "& h6": { margin: 0, flex: 1 },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTypography-caption": { width: "30%", textAlign: "center" },
    },
  },
  "& .content": {
    position: "relative",
    display: "flex",
    gap: 5,
    flexDirection: "column",
    "& strong": {
      fontWeight: 600,
      color: "#000",
      wordBreak: "normal",
    },
    "& span": {
      wordBreak: "break-word",
    },
    "& .pharmacyNote": {
      position: "absolute",
      right: 0,
      top: 0,
    },
    "& .MuiTypography-root": {
      display: "flex",
      gap: 10,
      color: "#333",
    },
    "& .MuiBox-root.sl": {
      display: "flex",
      justifyContent: "start",
      alignItems: "start",
      gap: 10,
      flexWrap: "wrap",
      flexDirection: "row",
    },
  },
}));

export const CommonCard = styled("div")(({ theme }) => ({
  padding: "0 16px 16px",
  backgroundColor: "#E3EBF0",
  border: "1px solid" + theme.palette.primary.main,
  borderRadius: "6px",
  "&.RxChange  ": {
    flex: 16,
    backgroundColor: "#FFFFFF",
    border: "none",
  },
  "&.Rx  ": {
    flex: 13,
    backgroundColor: "#FFFFFF80",
    border: "none",
  },
  "&.Rx  .title, &.RxChange  .title ": {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid" + DRxTheme.palette.primary.main + "50",
    marginBottom: 10,
    "& h6": { margin: 0, flex: 1 },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTypography-caption": { width: "30%", textAlign: "center" },
    },
  },
  "&.Rx  .content, &.RxChange  .content": {
    position: "relative",
    display: "flex",
    gap: 5,
    flexDirection: "column",
    "& strong": {
      fontWeight: 600,
      color: "#000",
      wordBreak: "normal",
    },
    "& span": {
      wordBreak: "break-word",
    },
    "& .pharmacyNote": {
      position: "absolute",
      right: 0,
      top: 0,
    },
    "& .MuiTypography-root": {
      display: "flex",
      gap: 10,
      color: "#333",
    },
    "& .MuiBox-root.sl": {
      display: "flex",
      justifyContent: "start",
      alignItems: "start",
      gap: 10,
      flexWrap: "wrap",
      flexDirection: "row",
    },
  },
  "&.pharmacyInfo": {
    display: "flex",
    gap: 10,
    padding: 10,
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      flexWrap: "wrap",
      justifyContent: "start",
    },
    "& .title": {
      display: "flex",

      alignItems: "center",
      gap: 5,

      justifyContent: "center",
    },
    "& .content": {
      [theme.breakpoints.up("sm")]: {
        borderLeft: "1px solid " + theme.palette.primary.main + "82",
      },
      [theme.breakpoints.down("sm")]: {
        borderTop: "1px solid " + theme.palette.primary.main + "82",
        paddingTop: 10,
        flexDirection: "column !important",
        alignItems: "start",
      },
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 10,
      paddingLeft: 10,
      [theme.breakpoints.down("md")]: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "start",
      },
      "& label": {
        fontWeight: 600,
        color: "#000",
      },
      "& .MuiTypography-root": {
        color: "#333",
      },
      "& .MuiBox-root": {
        display: "flex",
        justifyContent: "center",
        alignItems: "start",
        gap: 3,
        [theme.breakpoints.up("md")]: {
          flexDirection: "column",
        },
      },
      "& .MuiBox-root.sl": {
        display: "flex",
        justifyContent: "center",
        alignItems: "start",
        gap: 10,
        flexWrap: "wrap",
        flexDirection: "row",
      },
    },
  },
}));

export const CardTitle = styled(Box)(({ theme }) => ({
  width: "100%",
  "& .MuiTypography-root": {
    borderRadius: 6,
    padding: 10,
    fontWeight: 600,
  },
  "&.primary": {
    "& .MuiTypography-root": {
      background: theme.palette.primary.main,
      color: "#fff",
    },
  },
  "&.secondary ": {
    "& .MuiTypography-root": {
      background: theme.palette.secondary.main + "33",
      color: `${blackColor}`,
    },
  },
}));

export const ScrollBarStyle = styled("div")(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    "&.full": {
      maxHeight: 470,
      overflow: "auto",
      paddingRight: 5,
    },
    "&.half": {
      maxHeight: 400,
      overflow: "auto",
      paddingRight: 5,
    },
    "&.h300": {
      maxHeight: 300,
      overflow: "auto",
      paddingRight: 5,
    },
  },
}));
export const RxBox = styled("div")(({ theme }) => ({
  background: theme.palette.primary.main + "26",
  borderRadius: 6,
  marginTop: 10,
  border: "1px solid " + theme.palette.primary.main + "36",
  "&:nth-of-type(1) ": {
    marginTop: 0,
  },
  "& .titles.schdrug": {
    background: "#fff",
    filter: "contrast(0.9) brightness(0.9) sepia(.7)",
    border: "1px solid #CCCCCC",
    "& .MuiTypography-root": {
      color: "#000",
    },
  },
  "& .titles ": {
    background: theme.palette.primary.main,
    color: "#fff",
    "& .MuiTypography-root": {
      color: "#fff",
      fontWeight: 600,
    },
    borderRadius: "6px 6px 0 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
  },
  "& .content": {
    borderRadius: "0 0 6px 6px",
    color: "#19449A",
    fontWeight: 600,
    "&.MuiBox-root": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      flexWrap: "wrap",
      padding: 20,
      rowGap: 8,
      "& .MuiBox-root": {
        display: "flex",
        alignItems: "flex-start",
        rowGap: 8,
        columnGap: 5,

        "& textarea + p": {
          margin: 0,
        },
      },
      "& label + div": {
        minWidth: "60%",
      },
      "& .full": {
        width: "100%",
        "& label": {
          minWidth: 140,
          color: "red",
        },
      },
      "& .col1": {
        width: "28%",
        "& label": {
          minWidth: 140,
        },
      },
      "& .col2": {
        width: "42%",
        "& label": {
          width: 150,
        },
      },
      "& .col3": {
        width: "27%",
        "& label": {
          minWidth: 90,
        },
      },
      [theme.breakpoints.down("md")]: {
        "& .col1": {
          width: "100%",
          "& label": {
            minWidth: "inherit",
            width: "30%",
          },
        },
        "& .col2": {
          width: "100%",
          "& label": {
            width: "30%",
          },
        },
        "& .col3": {
          width: "100%",
          "& label": {
            minWidth: "inherit",
            width: "30%",
          },
        },
      },
    },
    "& .tooltip  ": {
      top: "0 !important",
      "& svg": {
        color: "#36A692",
        "&:hover": {
          color: "#fff",
        },
      },
    },
    "& .MuiTypography-root": {
      color: "#000",
    },
  },
}));

export const InputSPBoxStyle = styled(Box)(({ theme }) => ({
  "& .error": {
    background: `url(${ErrorIcon}) no-repeat center left`,
    paddingLeft: 20,
    color: "#706F6F",
  },
  [theme.breakpoints.up("md")]: {
    "& .mr": {
      marginLeft: 20,
    },
    "& .mm": {
      height: 40,
    },
    "& .error": {
      marginBottom: 5,
    },
  },
  display: "flex",
  marginBottom: 5,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",

    "&   label.MuiTypography-root": {
      display: "none",
    },

    " & .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
      display: "block !important",
    },
    "&   .MuiOutlinedInput-notchedOutline": {
      top: "-5px !important",
    },
  },
  [theme.breakpoints.up("sm")]: {
    width: 470,
  },
  maxWidth: "100%",
  justifyContent: "space-between",
  alignItems: "flex-start",
  "&  .MuiFormControl-root": {
    width: 333,
    maxWidth: "100%",
    marginTop: 0,
    marginBottom: 0,
    "&  .MuiFormControl-root": {
      marginBottom: 0,
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: 10,
      "&  .MuiFormControl-root": {
        marginBottom: 0,
      },
    },
  },
  "&  .MuiOutlinedInput-root ": {
    width: 333,
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      "&  .MuiInputBase-input ": { minWidth: "inherit" },
    },
  },
  "&  .info ": {
    marginLeft: 10,
  },
  " &  .MuiBox-root ": {
    position: "relative",
    "& .MuiTypography-root ": {
      marginTop: 7,
      color: theme.palette.primary.main,
    },
  },
  " & > .MuiBox-root ": {
    width: 333,
    maxWidth: "100%",
    "& .tooltip": {
      position: "absolute",
      right: "-30px",
      top: 3,
      "&:hover svg": {
        color: "#fff",
      },
    },
  },
  "& label.MuiTypography-root ": {
    lineHeight: "normal",
    marginTop: 3,
    "& label.MuiTypography-root  ": {
      lineHeight: "normal",
    },
  },
  "&  .nested.lm0   ": {
    "& label.MuiTypography-root  ": {
      marginTop: 0,
    },
  },

  "& > .nested   ": {
    width: 333,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "&  .MuiFormControl-root": { width: 111 },
    "&  .MuiOutlinedInput-root ": {
      width: 111,
    },
    "&  .MuiInputBase-input": {
      minWidth: "inherit",
    },
  },
  "& > .nested > .nested   ": {
    width: 210,
    maxWidth: "100%",
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  "& .nested   .MuiFormControl-root": { width: 110 },
}));

export const FiltersMenuStyle = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  "& .MuiIconButton-root:hover ": {
    background: "transparent",
  },
  "& .MuiTypography-root": {
    cursor: "pointer",
    color: theme.palette.primary.main,
  },
  "& img": {
    filter: theme.palette.filter.primary.main,
  },
}));
export const FiltersMenuXSStyle = styled(Menu)({
  "& .menu": {
    background: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    borderRadius: 2,
    overflow: "visible",
    filter: "drop-shadow(0px 2px 2px rgba(0,0,0,0.32))",
    minWidth: 230,
    border: "none !important",
    padding: "12px",
    marginTop: "-40px",
    marginLeft: "-40px",
    "& .MuiList-root": { padding: 0 },
    "& .MuiFormControlLabel-root": {
      margin: 0,
      padding: "5px 8px 5px  ",
      lineHeight: "32px",
      height: 32,
      border: "1px solid rgba(196, 196, 196, 0.5)",
      fontSize: "14px",
      color: "#000",
      borderRadius: 1,
      position: "relative",
    },

    "& .MuiFormControlLabel-root  span  ": {
      position: "static",
      zIndex: 2,
      background: "transparent",
    },
    "& .MuiFormControlLabel-root input + span svg, .MuiRadio-root": {
      color: "transparent",
      background: "transparent",
    },

    "& .MuiFormControlLabel-root input + span": {
      background: `url(${CheckOutlineIcon}) no-repeat center  center   `,
    },
    "& .MuiFormControlLabel-root:hover, & .MuiFormControlLabel-root.active": {
      background: "#EEEEEE",
    },
    "& .MuiFormControlLabel-root:hover input + span , & input:checked + span": {
      background: `url(${CheckedOutlineIcon}) no-repeat  center center   `,
    },
  },
});

export const SearchBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "15px",
  "&.header input ": {
    paddingLeft: "35px !important",
  },
  "& .MuiTextField-root": {
    marginBottom: "0 !important",
  },
  "&.searchicon  ": {
    "& label": {
      whiteSpace: "nowrap",
    },
    "&  .MuiButtonBase-root": {
      position: "absolute",
      right: 0,
      borderRadius: 6,
      "& svg": {
        width: 18,
        height: 18,
      },
      "&:hover": {
        color: "#fff",
      },
    },
  },

  "& .MuiIconButton-root:hover  MuiOutlinedInput-root ": {
    background: theme.palette.primary.main,
    color: "#fff !important",
  },
  "& .MuiOutlinedInput-root ": {
    paddingRight: 0,
  },
  "& .searchbtn  ": {
    width: 38,
    height: 38,
    marginLeft: "-38px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: 5,
  },
}));

export const SearchInput = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "15px",
  "& .MuiIconButton-root:hover   ": {
    background: theme.palette.primary.main,
    color: "#fff !important",
  },
  "& .MuiFormControl-root": {
    margin: "0 !important",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: 100,
  },
}));

export const DRxSelectStyle = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  "&.Supervisor ": {
    marginTop: -11,
    marginLeft: 35,
    [theme.breakpoints.down("md")]: { marginLeft: 26, marginTop: -14 },
    "& .MuiFormControl-root, & .MuiSelect-select": {
      width: "auto",
      minWidth: "inherit",
      padding: 0,
    },
    "& .MuiTypography-root": {
      display: "none",
    },
    "& div, & fieldset, & div:hover fieldset, & *:hover": {
      height: "auto !important",
      background: "transparent",
      outline: "none",
      border: "none !important",
      lineHeight: "normal",
    },
  },
  "& div[class*='MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root'].yellow .MuiSelect-select":
    {
      background: "#ffffa4 !important",
    },
  "& .MuiFormControl-root": {
    width: "100%",
  },
  "& .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
    display: "none",
  },
  "& .MuiOutlinedInput-input": {
    lineHeight: "34px",
  },
  "& .MuiOutlinedInput-input.MuiSelect-select": {
    borderRadius: "6px !important",

    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "normal",
    wordBreak: "break-all",
  },
  "& .MuiOutlinedInput-root": {
    padding: "0 !important",
    borderRadius: "6px !important",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    top: 0,
    borderRadius: "6px !important",
    border: "1px solid #ccc",
    borderWidth: "1px !important",
  },
  "& .MuiTextField-root .MuiInputLabel-root": {
    marginTop: "-9px !important",
    fontSize: 14,
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink ": {
    marginLeft: "10px !important",
    color: `${darkPrimaryColor}`,
    marginTop: "0 !important",
  },
  "& .MuiInputLabel-shrink + .MuiOutlinedInput-root fieldset legend ": {
    paddingLeft: "1px",
    marginLeft: "10px !important",
  },

  "& .MuiAutocomplete-root   ": {
    "& .MuiIconButton-root ": {
      marginTop: "0 !important",
    },
    "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
      padding: "0 40px 0 10px !important",
    },
    '& .MuiAutocomplete-clearIndicator[title="Clear"] ': {
      right: "16px !important",
    },
  },
  "& .MuiAutocomplete-endAdornment   ": {
    right: "2px !important",
    top: "-3px",
    "& svg[data-testid='ArrowDropDownIcon']  ": {
      fill: "transparent",
      backgroundImage: `url(${selectArrowIcon})`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
    },
  },

  "&.icon   ": {
    width: "calc(100% - 112px)",
    [theme.breakpoints.down("md")]: { width: "100%" },
    "&  .MuiFormControl-root .MuiOutlinedInput-root": {
      marginRight: 40,
    },
    "&  .MuiIconButton-root  ": {
      marginTop: "-3px",
    },
    "&  .MuiOutlinedInput-input.MuiSelect-select  ": {
      paddingRight: "40px !important",
      [theme.breakpoints.down("md")]: { paddingRight: "20px !important" },
    },
    "&  .MuiAutocomplete-root   ": {
      marginRight: 40,
    },
  },
  "& button.MuiButtonBase-root.MuiIconButton-root": {
    position: "absolute",
    top: 3,
    right: 0,
    zIndex: 1,
    background: "#EDF3F7",
    width: 34,
    height: 34,
    color: theme.palette.primary.main,
    borderRadius: 6,
    "&:hover ": {
      background: theme.palette.primary.main,
      color: "#fff",
      "& img ": {
        filter: `${filterWhiteColor}`,
      },
    },
  },
  "& button.MuiButtonBase-root.MuiIconButton-root svg": {
    width: 24,
    height: 24,
  },
  "&.w-auto  ": {
    "&  .MuiFormControl-root, & .MuiFormControl-root .MuiInputBase-formControl":
      {
        width: "auto",
        minWidth: "inherit",
      },
  },

  "&.autosearch .MuiAutocomplete-endAdornment": {
    right: 3,
    top: "-3px",
  },
  "&.mr10": {
    marginRight: 10,
  },
  "&.w100": {
    width: 100,
  },
}));

export const DRxInputStyle = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  "&.w100    ": {
    width: 100,
    "& input   ": {
      minWidth: 100,
    },
  },
  "&.OTPinput    ": {
    "& .MuiFormControl-root , & input   ": {
      width: 50,
      minWidth: "inherit",
      height: 50,
      fontSize: 18,
      textAlign: "center",
      [theme.breakpoints.down("md")]: { width: 40 },
    },
    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
      {
        appearance: "none",
        margin: 0,
      },
    "& .MuiTypography-root.error   ": {
      display: "none",
    },
  },
  "&.reportSearch .MuiButtonBase-root.MuiIconButton-root": {
    background: "transparent",
    top: 0,
  },
  "& .MuiFormControl-root  ": {
    width: "100%",
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box",
  },

  "& .MuiOutlinedInput-root fieldset legend, & label.MuiInputLabel-root": {
    display: "none",
  },
  "&.label  ": {
    "& .MuiOutlinedInput-root fieldset legend, &  label.MuiInputLabel-root": {
      display: "block",
    },
    "&   label.MuiInputLabel-root": {
      marginTop: "-11px !Important",
    },
    "&   label.MuiInputLabel-root.MuiInputLabel-shrink ": {
      marginTop: "-3px !Important",
    },
    "& .MuiOutlinedInput-notchedOutline ": {
      marginTop: "-9px !Important",
      top: 2,
    },
  },

  "& .MuiOutlinedInput-notchedOutline": {
    top: 0,
    borderRadius: 6,
    border: "1px solid #ccc",
  },

  "& .MuiOutlinedInput-root": {
    border: "none",
  },
  "& .MuiTextField-root .MuiInputLabel-root": {
    marginTop: "-9px !important",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink ": {
    marginLeft: "10px !important",
    color: `${darkPrimaryColor}`,
    marginTop: "0 !important",
  },
  "& .MuiInputLabel-shrink + .MuiOutlinedInput-root fieldset legend ": {
    paddingLeft: "1px",
    marginLeft: "10px !important",
  },
  "&.icon .MuiFormControl-root .MuiOutlinedInput-root": {
    marginRight: 40,
  },
  "&.icon .MuiIconButton-root  ": {
    marginTop: "-3px",
  },
  "& button.MuiButtonBase-root.MuiIconButton-root": {
    position: "absolute",
    top: 3,
    right: 0,
    zIndex: 1,
    background: "#EDF3F7",
    width: 34,
    height: 34,
    color: theme.palette.primary.main,
    borderRadius: 6,
    "&:hover ": {
      background: theme.palette.primary.main,
      color: "#fff",
      "& img ": {
        filter: `${filterWhiteColor}`,
      },
    },
  },
  "&.pwd button.MuiButtonBase-root.MuiIconButton-root ": {
    background: "none",
    right: 10,
    top: 0,
    "& img ": {
      filter: "none",
    },
  },
  "&.date button.MuiButtonBase-root.MuiIconButton-root, &.autosearch button.MuiButtonBase-root.MuiIconButton-root":
    {
      top: 0,
      background: "transparent",
      "&:hover ": {
        background: "transparent",
      },
    },
  "& button.MuiButtonBase-root.MuiIconButton-root svg": {
    width: 24,
    height: 24,
  },
  "&.w-auto .MuiOutlinedInput-input": {
    minWidth: "inherit",
  },
  "&.brs10 .MuiOutlinedInput-input , &.brs10 .MuiOutlinedInput-root": {
    borderRadius: "6px !important",
  },
  "&.mr10": {
    marginRight: 10,
  },
  "&.mt10": {
    marginTop: 10,
  },
}));

export const DRxCheckboxStyle = styled("div")(({ theme }) => ({
  "& .MuiCheckbox-root": {
    padding: "2px 5px 2px 10px",
  },
  "&.pr5": {
    paddingRight: 5,
  },
  [theme.breakpoints.up("md")]: {
    "&.fr": {
      textAlign: "right",
    },
  },
}));

export const DRxTextareaStyle = styled("div")(({ theme }) => ({
  width: "100%",
  "&  textarea ": {
    borderRadius: 6,
  },
  "&.h44 textarea ": {
    height: "44px !important",
  },
  "&.h50 textarea ": {
    height: "50px !important",
  },
  "&.sig textarea ": {
    height: "106px !important",
  },
  "&.note textarea ": {
    height: "50px !important",
  },
  "& textarea ": {
    height: "77px !important",
    width: "100% !important",
    padding: 10,
    boxSizing: "border-box",
    borderRadius: "6px !important",
    border: "1px solid #ccc",
    "&:hover ": { border: "1px solid " + theme.palette.primary.main },
    "&:focus-visible ": {
      border: "1px solid" + theme.palette.primary.main,
      outline: "none",
    },
  },
}));

export const FavPharmacyBtn = styled("div")({
  width: "100%",
  "& .MuiButton-root ": {
    height: "30px !important",
  },
});

export const RadioGroupStyle = styled("div")({
  "& .MuiFormControlLabel-label": {
    marginTop: "0 !important",
  },
});

export const SelectedReportList = styled("div")(({ theme }) => ({
  border: `1px solid ${lightGreyColor}`,
  borderRadius: 10,
  background: "#fff",
  marginTop: "0px",
  marginLeft: 0,
  position: "relative",
  zIndex: 1,
  "& .MuiFormGroup-root": {
    flexWrap: "nowrap",
    height: 126,
    overflow: "auto",
  },
  "& .MuiRadio-root ": {
    display: "none",
  },
  "&   .MuiTypography-root": {
    background: "#fff",
    width: "100%",
    padding: "4px 10px",
    marginTop: "0 !important",
  },
  "& .Mui-checked + .MuiTypography-root": {
    background: theme.palette.primary.main + "1f",
  },
  "&  .MuiFormControlLabel-root": {
    margin: 0,
  },
}));
export const ServiceStyle = styled("div")({
  display: "flex",
  columnGap: 5,
  marginBottom: 30,
  flexWrap: "wrap",
});
export const ServiceBox = styled("div")({
  width: 60,
  marginBottom: 10,
  marginRight: 10,

  "& .MuiCard-root, & .MuiIconButton-root": {
    width: "100%",
    height: 60,
    borderRadius: 100,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    background: "#f3f3f3",
    padding: 0,
    "& svg": {
      minWidth: 30,
      minHeight: 30,
      color: "#999",
    },
  },

  "&.active .MuiCard-root, &:hover .MuiCard-root": {
    background: "#E59E33",
    "& svg, &  img ": {
      minWidth: 25,
      minHeight: 25,
      color: "#fff",
      filter: `${filterWhiteColor}`,
    },
  },
  "& .MuiTypography-root": {
    fontSize: 13,
    textAlign: "center",
    marginTop: 10,
  },
});

export const ReportBox = styled("div")({
  padding: 10,
  marginBottom: 20,
  marginRight: 20,
  border: "1px solid #c4c4",
  borderRadius: 10,
});

export const NewUpdatesStyle = styled("div")(({ theme }) => ({
  paddingRight: 10,
  display: "flex",
  columnGap: 5,
  flexWrap: "wrap",

  borderRadius: 10,
  maxHeight: 500,
  overflow: "auto",
  "& .MuiBox-root:nth-of-type(1)": {
    marginTop: 0,
  },
  "& .MuiBox-root": {
    cursor: "pointer",
    background: "#F6F6F6",
    marginTop: 10,
    width: "100%",
    padding: "5px 10px",
    borderRadius: 6,
    "&:hover ": {
      background: theme.palette.primary.main,
      "& .date, & .MuiTypography-root": {
        color: "#fff",
      },
    },
  },
  "& .date": {
    fontSize: 12,
    color: "#999",
  },
  "& .MuiTypography-root": {
    fontWeight: 600,
  },
}));

export const LetterSearchStyle = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  gap: 3,
  justifyContent: "center",
  marginBottom: 30,
  flexWrap: "wrap",
  "& button ": {
    border: "none",
    background: "#eee",
    display: "inline-block",
    fontWeight: 600,
    padding: "3px 8px",
    cursor: "pointer",

    "&:hover   , &.active": {
      background: theme.palette.primary.main + "!important",
      color: "#fff",
    },
    "&:disabled , &:disabled:hover  ": {
      background: "transparent !important",
      color: "#999",
      cursor: "default",
      fontWeight: 400,
    },
  },
}));
export const TimeCheckboxStyle = styled(Box)(({ theme }) => ({
  display: "flex",
  "& .MuiIconButton-root": {
    background: theme.palette.primary.main,
    color: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
    width: 40,
    height: 40,
    marginRight: 30,
    "& svg": {
      width: 30,
      height: 30,
    },
  },
}));

export const CollapsibleStyle = styled("div")(({ theme }) => ({
  borderRadius: 6,
  overflow: "auto",
  marginBottom: 10,

  "& .MuiTypography-subtitle1 ": {
    cursor: "pointer",
    background: "#F6F6F6",
    color: theme.palette.primary.main,
    padding: "6px 12px",
    borderRadius: 6,
    display: "flex",
    alignItems: "fle-start",
    justifyContent: "space-between",
    columnGap: 10,
    width: "100%",
    "& svg  ": {
      marginTop: 5,
    },
    "& span  ": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      columnGap: 5,
      fontWeight: "600",
    },
  },
  "& .active .MuiTypography-subtitle1 ": {
    background: theme.palette.primary.main,
    color: "#fff",
  },
  "&   .MuiTypography-body2 ": {
    marginBottom: 10,
  },
  "& .content  ": {
    padding: 12,
    background: "#fff",
  },
}));

export const FaqStyle = styled("div")(({ theme }) => ({
  borderRadius: 6,
  overflow: "auto",
  marginBottom: 10,
  border: "1px solid #ccc",
  padding: 10,
  minHeight: 40,
  boxSizing: "border-box",
  "&.active  ": {
    border: "1px solid " + theme.palette.primary.main,
    "& svg  ": {
      transform: "rotate(180deg)",
    },
  },
  "& .header .MuiTypography-root ": {
    cursor: "pointer",
    fontWeight: 600,

    borderRadius: 6,
    display: "flex",
    alignItems: "fle-start",
    justifyContent: "space-between",
    columnGap: 10,
    width: "100%",
    "& img  ": {
      width: 15,
    },
    "& svg  ": {
      marginTop: 6,
    },
  },
  "&.active .header .MuiTypography-root img  ": {
    transform: "rotate(180deg)",
  },
  "& .content  ": {
    display: "flex",
    flexDirection: "column",
    marginTop: 6,
    gap: 6,
    "& .MuiTypography-body2 ": {
      color: "#4A4A4A",
      marginBottom: 5,
    },
  },
}));
export const NoteToggleStyle = styled(Box)(({ theme }) => ({
  display: "flex",
  "& .MuiFormGroup-root ": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    "& label:first-of-type": {
      marginRight: 8,
    },
    "& .MuiFormControlLabel-root": {
      margin: 0,
    },
    "& input + span, & svg, & .MuiRadio-root": {
      display: "none",
    },
    "& .MuiRadio-root + .MuiTypography-root": {
      color: theme.palette.primary.main,
      fontSize: 14,
      marginBottom: 2,
      background: "#fff",
      padding: "3px 10px",
      borderRadius: 6,
      height: 27,
      lineHeight: "19px",
      boxSizing: "border-box",
      border: "1px solid " + theme.palette.primary.main,
    },
    "& .Mui-checked + .MuiTypography-root": {
      color: "#fff",
      background: theme.palette.primary.main,
    },
  },
}));

export const Hints = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  borderRadius: 10,
  padding: 10,
  gap: 10,
  border: "1px solid " + theme.palette.primary.main + "26",
  background: "#fff",
  "& .MuiTypography-root": {
    display: "flex",
    alignItems: "center",
  },
  "& .ftp svg": {
    color: "#F3F781",
  },
  "& .efp svg": {
    color: "#FFC0CB",
  },
  "& .op svg": {
    color: "#FFCC99",
  },
}));

export const LogoutStyle = styled("div")({
  display: "flex",
  flexDirection: "column",
  textAlign: "center",
  marginBottom: 10,
  "& div": {
    fontSize: 12,
    marginTop: "-5px",
  },
});
export const RightSidebarStyle = styled(Box)(({ theme }) => ({
  width: 64,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    flex: "none",
    "& .chatbox": {
      background: theme.palette.background.default + "!important",
      border: "none !important",
      padding: "0 !important",
      width: "100%",
    },
  },
  [theme.breakpoints.up("md")]: {
    padding: "5px 5px 0 5px",
  },
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: 8,
  boxSizing: "border-box",
  "& > svg": {
    color: theme.palette.primary.main,
  },

  "& > div.rightsidebar > .MuiBox-root": {
    width: 54,
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #DFDDDD",
    background: "#fff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    gap: 0,
    borderRadius: 6,
    "& div": {
      height: "auto",
      width: "auto",
    },
    "&  .MuiIconButton-root  ": {
      padding: 0,
      [theme.breakpoints.up("md")]: {
        marginTop: 20,
      },
      [theme.breakpoints.down("md")]: {
        marginTop: 5,
      },
      width: 24,
      height: 24,

      "& a": {
        minWidth: 34,
        padding: "6px 3px 3px",
        borderRadius: 6,
        transition: theme.transitions.create(["width", "transform"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      },
      "&:hover  ": {
        background: "transparent",
        "&   a": {
          color: theme.palette.primary.main,
          background: "#eee",
          transform: "scale(1.1)",
        },
      },
    },
  },
}));

export const BenefitsBtnStyle = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  marginTop: 5,
  marginBottom: 5,

  [theme.breakpoints.up("md")]: {
    paddingLeft: 115,
  },
  gap: 8,
  "& button": {
    margin: 0,
    "&  img ": {
      filter: "brightness(1)",
    },
  },
}));

export const RxPadBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: { flexDirection: "column" },
  "&.mt5": {
    marginTop: 5,
  },
  "&.mt7": {
    marginTop: 7,
  },
  "& .pl115": {
    [theme.breakpoints.up("md")]: { paddingLeft: 115 },
  },
  "& .mdmw": {
    minWidth: 115,
  },
  "& .smmw": {
    minWidth: 76,
    [theme.breakpoints.down("md")]: { minWidth: 115 },
  },
  "& .xsmw": {
    minWidth: 65,
    [theme.breakpoints.down("md")]: { minWidth: 115 },
  },
}));
export const HelpCardStyle = styled(Box)(({ theme }) => ({
  border: "1px solid #ccc",
  width: "100%",
  height: "100%",
  borderRadius: 6,
  marginBottom: 10,
  boxShadow: "0px 2px 4px rgb(0 0 0 / 10%)",
  "& .MuiTypography-subtitle2 ": {
    background: "#F6F6F6",
    padding: "6px 12px",
    borderRadius: 6,
    display: "flex",
    alignItems: "fle-start",
    justifyContent: "space-between",
    columnGap: 10,
    width: "100%",
    margin: 0,
  },
  "& .content  ": {
    padding: 12,
    background: "#fff",
  },
  "& .MuiButton-startIcon  ": {
    marginRight: 3,
  },
  "& .MuiButton-root  ": {
    [theme.breakpoints.up("md")]: {
      fontSize: 13,
    },
    [theme.breakpoints.down("md")]: {
      fontSize: 12,
    },
    color: theme.palette.primary.main,
  },
}));

export const HelpTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: 10,
    backgroundColor: "#fff",
    borderRadius: 6,
    color: "#333",
    maxWidth: 320,
    boxShadow: "0px 2px 6px rgb(0 0 0 / 25%)",
    "& .icons": {
      display: "flex",
    },
    "& span:before": {
      backgroundColor: "#fff",
      boxShadow: "0px 2px 6px rgb(0 0 0 / 25%)",
    },

    "& .MuiTypography-body2": {
      fontSize: 13,
    },
    "& .MuiTypography-body2.textwrap": {
      textOverflow: "unset",
      whiteSpace: "normal",
    },
    "& .MuiIconButton-root ": {
      marginLeft: 5,
      "&:hover": {
        background: "#eee",
        color: "#fff !important",
      },
    },
  },
}));

export const CheckboxField = styled("div")({
  margin: "5px 5px 5px 0",
  "& label ": {
    padding: "5px 5px 5px 25px",
  },
  "& input": {
    display: "none",
  },
  "& input + label": {
    background: `url(${CheckFillIcon}) no-repeat center left`,
  },
  "& input:checked  + label": {
    background: `url(${CheckedFillIcon}) no-repeat center left`,
  },
});

export const TabBtns = styled("div")({
  paddingLeft: 10,
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "flex-start",
});

export const IconBtn = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 46,
  height: 46,
  background: "#fff",
  borderRadius: 40,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  "& .MuiIconButton-root": {
    padding: 11,
    height: "100%",
    width: "100%",
  },
  "& .MuiIconButton-root:hover": {
    color: "#fff",
  },
  "& .MuiBadge-badge": {
    right: "0px !important",
    top: "-10px  !important",
  },
});

export const ClinicLocStyle = styled(Box)(({ theme }) => ({
  background: "transparent",
  boxShadow: "none",
  display: "flex",
  justifyContent: "start",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    borderTop: "1px solid #c4c4c4",
    padding: "2px 0 0",
  },
  "& div.MuiTypography-body2": {
    minHeight: 20,
    [theme.breakpoints.up("md")]: {
      marginLeft: 20,
    },
    [theme.breakpoints.down("md")]: {
      "& span": { display: "none" },
      "& img": { height: 18 },
      lineHeight: "normal !important",
    },
    marginRight: 10,

    whiteSpace: "nowrap",
    lineHeight: "35px",
  },

  "& .MuiBox-root": {
    width: "100%",
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      "& .MuiFormControl-root": {
        marginTop: 3,
      },

      "& .MuiFormControl-root, & .MuiSelect-select, & fieldset, & div:hover fieldset, & *:hover":
        {
          height: "auto !important",
          background: "transparent",
          outline: "none",
          border: "none !important",
          lineHeight: "normal",
          padding: 0,
        },
    },
  },
  "& svg, & img": {
    verticalAlign: "middle",
    marginRight: 5,
    filter: theme.palette.filter.primary.main,
  },
}));
export const OnBehalfStyle = styled(Box)(({ theme }) => ({
  background: "transparent",
  boxShadow: "none",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    borderTop: "1px solid #c4c4c4",
    padding: "3px 0 0",
  },
  "& .onbehalf": {
    display: "flex",
    alignItems: "center",
    paddingRight: 10,
    "& .MuiButton-text": {
      minWidth: "inherit",
      padding: 0,
      marginLeft: 10,
    },
  },
  "& .super": {
    "& .MuiTypography-body2  .MuiTypography-body2": {
      marginTop: -8,
    },
    "& .MuiTypography-body2 img": {
      marginTop: 10,
    },
  },
  "& .MuiTypography-body2": {
    minHeight: 20,
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontWeight: 600,
    lineHeight: "35px",

    "& .MuiTypography-body2": {
      display: "inline-block",
      [theme.breakpoints.down("md")]: {
        fontSize: 12,
      },
      [theme.breakpoints.up("md")]: {
        marginLeft: 5,
      },
      lineHeight: "normal",
    },
    "& svg, & img": {
      verticalAlign: "middle",
      marginRight: 5,
      filter: theme.palette.filter.primary.main,
      [theme.breakpoints.down("md")]: {
        height: 20,
      },
    },
  },
}));

export const TooltipCardStyle = styled(Box)(({ theme }) => ({
  padding: 14,
  backgroundColor: "#fff",
  borderRadius: 6,
  color: "#000",
  maxWidth: 288,
  minWidth: 288,
  maxHeight: 68,
  display: "flex",
  justifyContent: "space-between",
  position: "absolute",
  zIndex: 99,
  left: 0,
  top: 30,
  marginTop: 5,
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  alignItems: "center",
  "& > div": { position: "relative" },
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.palette.primary.main + `21`,
  },
  "& a": {
    color: "#000",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  "&.email ": {},
  " &.phone": {
    minWidth: 188,
    " & .MuiTypography-root": {
      letterSpacing: 1,
    },
  },
  "&.email, &.phone": {
    "& > div": { width: "100%" },
  },
  "& .closeIcon, & .closeIcon:hover": {
    marginLeft: 5,
    background: "#fff",
    color: theme.palette.primary.main,
    width: 40,
    height: 40,
    "& img ": {
      filter: theme.palette.filter.primary.main,
    },
  },
}));

export const RxTabsStyle = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  position: "relative",
  "&:before": {
    content: `""`,
    width: "100%",
    position: "absolute",
    bottom: 8,
    borderBottom: "1px solid #ccc",
  },
  "& .MuiTabs-root": {
    position: "relative",
    marginBottom: 4,
    minHeight: 40,

    "& .MuiTabs-flexContainer": {
      position: "relative",
      bottom: -5,
    },
    "& .MuiButtonBase-root": {
      minHeight: "inherit",
      minWidth: "inherit",
      padding: 6,
      textTransform: "none",
      marginRight: 10,
      justifyContent: "space-between",
      "& .MuiButtonBase-root": {
        width: "auto",
        padding: 5,
        margin: 0,
        marginLeft: 6,
      },
    },
    "& .MuiTabScrollButton-root ": {
      padding: 6,
      width: "auto",
    },

    "& .MuiTabs-scroller ": {
      [theme.breakpoints.up("lg")]: {
        maxWidth: 750,
      },
      [theme.breakpoints.between("md", "lg")]: {
        maxWidth: 650,
      },
      [theme.breakpoints.between("sm", "md")]: {
        maxWidth: 500,
      },
      [theme.breakpoints.between("xs", "sm")]: {
        maxWidth: 300,
      },
      [theme.breakpoints.down("xs")]: {
        maxWidth: 200,
      },
    },
    "& .remove:hover img": {
      filter: `${filterWhiteColor}`,
    },
    "& .MuiTabs-indicator": {
      background: theme.palette.primary.main,
      height: 5,
      borderRadius: "0 0 4px 4px",
    },
  },

  "& .MuiButtonBase-root.summary-btn": {
    padding: 5,
    minWidth: 80,
    border: "none",
    background: "none",
    "&.active": {
      color: theme.palette.primary.main,
    },
    "&.active:before": {
      content: `""`,
      width: "100%",
      borderRadius: "0 0 4px 4px",
      position: "absolute",
      height: 5,
      background: theme.palette.primary.main,
      bottom: 1,
    },
  },
}));

export const LgCardStyle = styled(Box)(({ theme }) => ({
  background: "#F6F6F6",
  borderRadius: 6,
  padding: "8px 10px",
  marginBottom: 6,
  boxSizing: "border-box",
  "&.htstr ": {
    height: "98%",
  },
  [theme.breakpoints.up("md")]: {
    "&.rft ": {
      marginLeft: 6,
    },
  },
}));

export const IframeStyle = styled(Box)(({ theme }) => ({
  "& iframe ": {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: "100%",
    height: "100%",
    border: "1px solid #ccc",
  },
  minHeight: "480px",
  position: "relative",
}));
export const AdverseEffectsStyle = styled(Box)(({ theme }) => ({
  "& .MuiTableContainer-root ": {
    margin: "5px auto",
    border: "1px solid #c4c4c4",
    boxShadow: "none",
  },
  "& .MuiTableHead-root ": {
    background: theme.palette.primary.main + "3c",
  },
  "& .MuiTableCell-root ": {
    padding: "5px 10px",
  },
}));
export const PharmacyBenefitsStyle = styled(Box)(({ theme }) => ({
  padding: 10,

  "& .section ": { marginBottom: 20 },
  "& .MuiGrid-container ": {
    border: "1px solid #c4c4c4",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 10,
    overflow: "hidden",
    marginTop: 5,
  },
  "& .title ": {
    padding: "5px 10px",
    background: theme.palette.primary.main + "3c",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 6,
  },
  "& .MuiGrid-item ": {
    border: "1px solid #c4c4c4",
    borderLeft: "none",
    borderTop: "none",
    padding: "5px 10px 2px",
  },
  "& .MuiTypography-root ": {
    wordBreak: "break-word",
  },
  "& .MuiTypography-subtitle2 ": {
    marginTop: 10,
  },
}));
export const ChatbotStyle = styled(Box)(({ theme }) => ({
  boxShadow: "none",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    width: "100% !important",
  },
  "& .chatIcons": {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      justifyContent: "space-around",
      flexDirection: "row-reverse",
      alignItems: "center",
      padding: "4px 8px",
      width: "100%",
      boxSizing: "border-box",
      minHeight: 48,
      gap: 0,
    },
    gap: 1,
    border: "none",

    [theme.breakpoints.up("md")]: {
      "& .MuiButtonBase-root ": {
        marginTop: 10,
      },
      "& .phone ": {
        marginBottom: 15,
      },
    },

    "& .MuiButtonBase-root": {
      height: 36,
      width: 36,
      transform: "scale(1.0)",
      "& img": {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
      },
      "&:hover": {
        transform: "scale(1.1)",
        background: "transparent",
      },
      [theme.breakpoints.down("md")]: {
        height: 40,
        width: 40,
        transform: "none",
        margin: "0 !important",
        padding: 6,
        "&:hover": {
          transform: "none",
          background: "transparent",
        },
      },
    },
  },
  "& .chatwidget": {
    boxShadow: "0px 1px 11px 2px rgb(0 0 0 / 20%)",
    position: "fixed",
    right: -620,
    bottom: 70,
    zIndex: 1,
    display: "none",
    transition: "all 0.16s ease-in-out 0s",
    [theme.breakpoints.up("md")]: {
      maxWidth: 345,
      "&.active": { right: 5 },
    },
    [theme.breakpoints.down("md")]: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      maxWidth: "inherit",
      zIndex: 99999999999,
    },
    "&.active": { display: "block" },
    "& .MuiCardHeader-root": {
      padding: 6,
      display: "flex",
      ailgnItems: "center",
      height: 30,
      "& .MuiTypography-root": {
        fontWeight: 600,
        color: "#fff",
      },
      "& .MuiButtonBase-root  svg  ": {
        color: "#fff",
        fill: "#fff",
      },
      "& .MuiButtonBase-root:hover  svg  ": {
        color: "#fff",
        fill: "#fff",
      },
      "& .MuiAvatar-root": {
        marginLeft: 10,
        background: theme.palette.primary.main,
        color: "#fff",
        borderRadius: 6,
        "& svg, & img": {
          height: 25,
          width: 25,
        },
      },
      background: theme.palette.primary.main,
    },
    "& .MuiCardContent-root ": {
      gap: 10,
      [theme.breakpoints.up("md")]: { height: "300px !important" },
      [theme.breakpoints.down("md")]: { height: "calc(100% - 100px)" },
      overflow: "auto",
      "& .MuiAvatar-root": {
        background: theme.palette.primary.main,
        color: "#fff",
        borderRadius: 6,
      },
      "& .bubble ": {
        display: "flex",
        flexDirection: "column",
        marginBottom: 10,
        borderRadius: 10,
        gap: 10,
        flexWrap: "nowrap",
      },
      "& .drx": {
        paddingBottom: 10,
        position: "relative",
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "row",
        flexWrap: "wrap",
        paddingLeft: 30,
        "& .user_icon": {
          position: "absolute",
          marginLeft: -35,
          marginRight: 10,
          height: 30,
          width: 30,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100%",
        },
        "& .time": {
          position: "absolute",
          left: 45,
          bottom: 8,
          fontSize: 12,
          color: "#999",
        },
      },
      "& .user": {
        paddingBottom: 10,
        position: "relative",
        display: "flex",
        justifyContent: "flex-end",
        flexDirection: "row",
        flexWrap: "wrap",
        marginLeft: "10%",
        "& .time": {
          position: "absolute",
          right: 0,
          bottom: 8,
          fontSize: 12,
          color: "#999",
        },
      },
      "&  .text ": {
        padding: "6px 10px",
        borderRadius: 6,
        marginTop: 0,
      },
      "& .drx .text": {
        background: theme.palette.primary.main + "1a",
      },
      "& .user .text": {
        background: theme.palette.primary.main + "3b",
      },
      "&  .btns": {
        width: "100%",
        display: "flex",
        gap: 5,
        flexWrap: "wrap",
      },
      "& .btn": {
        color: "#fff",
        background: theme.palette.primary.main,
        borderRadius: 6,
        lineHeight: "normal",
        padding: "4px 14px",
        whiteSpace: "normal",
        fontSize: 14,
        fontWeight: 600,
        opacity: 0.9,
        margin: "0 5px 5px 0",
        cursor: "pointer",
        "&:hover": { opacity: 1 },
      },
    },
    "& .MuiCardActions-root": {
      display: "flex",
      alignItems: "center",
      "& img": {
        margin: 2,
        filter: theme.palette.filter.primary.main,
        cursor: "pointer",
      },
      "& input": {
        borderRadius: 6,
        border: "1px solid #c4c4c4",
        padding: "6px 10px",
        marginRight: 5,
        [theme.breakpoints.up("md")]: { width: 300 },
        [theme.breakpoints.down("md")]: { width: " 100% " },
      },
    },
  },
}));
export const DrugFoodStyle = styled(Box)(({ theme }) => ({
  padding: 10,
  "& .MuiGrid-container ": {
    border: "1px solid #c4c4c4",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 6,
    overflow: "hidden",
    marginTop: 5,
  },
  "& .MuiGrid-item.lbl ": {
    background: "#f3f3f3",
  },
  "& .MuiGrid-item ": {
    border: "1px solid #c4c4c4",
    borderLeft: "none",
    borderTop: "none",
    padding: "5px 10px 2px",
  },
  "& .MuiTypography-root ": {
    margin: 3,
  },
}));

export const GuideTourBtnStyle = styled(Box)(({ theme }) => ({
  "&.rxpad": {
    position: "relative",
    top: -9,
  },
  "& .MuiButtonBase-root.guide-tour-btn": {
    color: theme.palette.primary.main,
    margin: "auto",
    fontSize: 10,
    padding: "30px 3px 5px 3px",
    lineHeight: "12px",
    fontWeight: 600,
    width: "100%",
    whiteSpace: "normal",
    minWidth: "inherit",
    height: "auto",
    background: ` #fff url(${TourIcon}) no-repeat center 4px`,

    backgroundSize: 26,
    boxShadow: "0px 2px 4px rgb(0 0 0 / 10%)",
  },
}));

export const ClassicThemeStyle = styled("div")({
  display: "flex",
  flexDirection: "column",
  textAlign: "center",

  "& img": {
    width: 24,
    margin: "10px auto",
    cursor: "pointer",
  },
  "& div": {
    fontSize: 12,
    marginTop: "-5px",
  },
});

export const SwitchThemeStyle = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",

  "& .screenshotContainer": {
    height: "auto",
    marginRight: 20,
    maxWidth: "100%",
    background: theme.palette.primary.main,
    color: "#fff",
    "& .screenshot img": {
      height: "auto",
      maxWidth: "100%",
      border: "1px solid " + theme.palette.primary.main,
    },
    "& .MuiTypography-caption": {
      padding: "0px 10px 6px",
      fontSize: 12,
      textAlign: "center",
    },
  },
}));

export const PDMPMSG = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  left: 0,
  bottom: 0,
  margin: "-10px",
  padding: "8px",
  background: `linear-gradient(0deg, ${theme.palette.secondary.main} 0%, ${
    theme.palette.primary.main + 95
  } 100%)`,
  borderRadius: "5px",
  "& .MuiTypography-root": {
    fontSize: 10,
    textAlign: "center",
    color: "#fff",
    lineHeight: "12px",
  },
}));

export const IDMEPopupStyle = styled("div")(({ theme }) => ({
  "& .logos": {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  "& .success": {
    display: "flex",
    gap: 20,
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    margin: 20,
  },
  "& .timer": {
    display: "flex",
    gap: 20,
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "50%",
    margin: "20px auto",
    width: 150,
    height: 150,
  },
  "& .iconCheckboxes": {
    display: "flex",
    gap: 20,
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    margin: "auto",
    width: 650,
    minHeight: 200,
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: { flexDirection: "column" },
    "& .iconCheckbox": {
      position: "relative",
      maxWidth: 250,
      minWidth: 250,
      minHeight: 220,
      maxHeight: 200,
      flex: 1,
      // [theme.breakpoints.up("md")]: { maxWidth: 250 },
      "& .checkmark": {
        display: "none",
        position: "absolute",
        right: 0,
        top: 0,
      },
      "&.setup": {
        padding: 16,
        minHeight: 30,
      },
      "& .icon": {
        color: "#ccc",
        width: 60,
      },
      "&.active": {
        background: "#EBF1F4",
        border: "1px solid #44799C",
        "& .checkmark": {
          display: "block",
        },
        "& .icon": {
          color: "#44799C",
        },
        "&.setup": {
          padding: 16,
          minHeight: 30,
        },
      },
      "&:hover": {
        background: "#EBF1F4",
        border: "1px solid #44799C",

        "& .icon": {
          color: "#44799C",
        },
      },
      display: "flex",
      flexDirection: "column",
      gap: 10,
      justifyContent: "flex-start",
      alignItems: "flex-start",
      textAlign: "left",
      border: "1px solid #ccc",
      borderRadius: 10,
      padding: 24,
      cursor: "pointer",
    },
  },
}));
